/**
 * app.js — /remove-object-from-video page controller.
 *
 * Decisions that came out of measurement rather than taste:
 *
 *  1. ONE TAP, not keyframes. The sibling tool ships manual keyframes as the
 *     supported path for anything that moves; over 30 days 1811 sessions marked
 *     a region and 24 ever placed a keyframe. So the tap runs a real track and
 *     the keyframes are an OUTPUT, not a chore.
 *
 *  2. THE TAP IS ANSWERED BY A SEGMENTER (2026-09-09). EdgeTAM (Meta,
 *     Apache-2.0, ~20 MB fp16) in the worker on WebGPU or WASM answers with a
 *     silhouette on ANY footage; the arithmetic "differs from the temporal
 *     median" selector it replaced said "nothing-at-tap" 1311 times of 1527.
 *
 *  3. NOTHING WAITS FOR THE ESTIMATE (2026-09-13). The coverage estimate —
 *     how much of the hole the engine rebuilds from the user's own frames —
 *     used to stand between the mark and the export button: p50 17 s, p90 59 s,
 *     and the only line on screen during it said «считаю…» forever, because
 *     nothing replaced it when the number arrived. The report that started
 *     this rewrite («не понимаю что делать дальше после анализа») came from a
 *     session that got 0 %, pressed «− Исключить» four times — a mode switch
 *     that does nothing by itself — and left. 139 sessions reached the
 *     estimate in three days; 41 exported. Now the export button is live the
 *     moment an object is marked, the estimate runs behind it and fills in,
 *     and the instruction for the current state is ON the stage.
 *
 *  4. SHOW, THEN COUNT. Right after the mark, one frame of the actual result
 *     is rendered by the same engine and shown as a before/after wipe. A
 *     percentage is a claim; a frame is evidence.
 *
 *  5. THE RESULT IS DELIVERED, NOT OFFERED. 726 finished exports on the
 *     sibling tool produced 106 downloads; /video-blur, which saves the file
 *     itself, delivered 201 of 242. The file saves on completion.
 *
 *  6. SEVERAL OBJECTS, ONE PASS. The engine always took an array of regions;
 *     the page gave it one, and the FAQ told people to export, re-upload and
 *     mark the second object — two renders and a generation of re-encoding.
 *
 *  7. THE PHONE'S THREE DEATHS ARE THREE DEFECTS (refund #32, 16–17.09.2026,
 *     Galaxy S22 / Adreno 730 / 8 GB / Chrome 150). One session, read from
 *     telemetry: (a) WebGPU answered every prompt with a frame-sized blob and
 *     the page blamed her aim — closed 16.09 by the known-answer ladder and
 *     the recheck-on-first-wrong-answer (decision notes by segInit /
 *     recheckSegAfterWrongAnswer); (b) the render fell to <video> seeking
 *     because the SHARED extractor put Android's non-existent software
 *     decoder first and did not retry its asynchronous refusal in range mode
 *     — closed 17.09 in webcodecs-frame-extractor.js (hardware first
 *     everywhere, `decoder-error:…:decoded=0` retry-eligible), guard
 *     tests/playwright/frame-extractor-retries-a-lying-decoder.mjs; (c) the
 *     renderer DIED 30 s and 5 s into two renders — `run_lost_on_reload
 *     nav=reload hidden=0`, eight such sessions in fourteen days, all phones
 *     — and no sensor of ours can name the cause, because they die with it.
 *     The browser can: the Reporting API `crash` report (reason oom |
 *     unresponsive) now lands as feature_use `renderer_crash` on the tool
 *     (SecurityHeaders + AnalyticsController::reports). Read that before
 *     guessing at memory.
 *
 *  8. THE FILL REACHES THE WHOLE CLIP (17.09.2026). Every object goes to the
 *     engine as `moving:true, forceWindow:true`, and the window tier read
 *     only the ring — ±12 frames, 0.4 s. A person crossing a locked-off shot
 *     in eight seconds is still on their own pixels in every neighbour, so
 *     the whole silhouette was a hole for Telea: the grey smear Farid saw on
 *     the bench clips, and what 1340 of 2870 coverage estimates (< 20 % real)
 *     had been promising for a month. The sample frames read for tracking
 *     (pickPlateSamples → session.setSamples) now join every reconstruction as
 *     candidates under their own masks and warps, admitted through a plate
 *     built from the samples themselves (recon-worker longReachSources).
 *     Static crosswalk, invented pixels per render: 543 809 → 78. A hand-held
 *     clip whose motion an affine cannot follow gets no reach and no smear.
 *
 *  9. A SMALL OBJECT'S BOX IS ANSWERED ON A NATIVE CROP (17.09.2026). The
 *     analysis grid is 480 px; a person at 5 % of a 720p frame is 24×65 px
 *     there and the segmenter cut his legs off (box) or kept the torso (tap).
 *     For a small BOX the worker asks the page for that region at native
 *     resolution (serveCrop, a hidden video element) and picks the head that
 *     fills the box: the whole figure. Every wider use was measured on the
 *     sprite bench and rejected — a tap re-asked on a crop answered the car
 *     behind the man and declined a static sprite; crops for the tracker's own
 *     prompts dropped a sprite track to IoU 0.15 and grew silhouettes to twice
 *     the man. What remains open is following a 5 % figure through a crowd:
 *     the NCC tracker jumps to look-alikes and the continuity gate judges
 *     shape, not position — a memory tracker (EdgeTAM's video half) is the
 *     next project, not another heuristic.
 */
(function () {
    'use strict';
    // window.__perPixel / window.__setText (timbrica-core.js) write a position or a text only when what is
    // shown changes. A page built before they existed can still be handed this file — it then writes the
    // plain way. Why: docs/why/primitives/per-pixel-writes.md.
    function textWrite(e, t) { if (window.__setText) return window.__setText(e, t); e.textContent = t; }


    var CFG = window.RO_CFG || {};
    var T = CFG.i18n || {};
    var $ = function (id) { return document.getElementById(id); };
    var ae = function (ev, data) { try { window.__ae && window.__ae(ev, data); } catch (e) { } };
    var feat = function (f, d) { ae('feature_use', Object.assign({ f: f }, d || {})); };
    var toast = function (m, t) { try { window.__toast && window.__toast(m, t || 'info'); } catch (e) { } };
    var stage = function (s, d) { try { window.__stage && window.__stage(s, d); } catch (e) { } };
    var tr = function (key, vars, fallback) {
        var s = T[key] || fallback || '';
        if (vars) Object.keys(vars).forEach(function (k) { s = s.split(':' + k).join(vars[k]); });
        return s;
    };

    var PROC_MAX = 480;          // analysis resolution — same as the engine's
    var TRACK_FPS = 5;           // track samples per second of clip
    /* Without WebGPU the encoder costs 2–5 s per frame on a desktop CPU (bench
       2026-09-09, single-thread WASM), so the silhouette is refreshed once a
       second and the box tracker carries the samples in between. */
    var SEG_EVERY_WASM = 5;
    /* …that was by the provider's NAME. The cost is a property of the device:
       prod 09–16.09 (RU), one encode+decode — Windows WebGPU p50 0.4 s, Android
       WebGPU 1.1 s (p90 3.6 s, one realme: 5 s), Android WASM 1.6 s, iOS WASM
       1.7 s. Every tap measures exactly that on exactly this device, so the
       cadence follows the measurement: one silhouette per SEG_BUDGET_MS of
       work, between one per sample and one per eight. A 30-sample track is
       then bounded near 30 × budget instead of 30 × whatever the GPU takes
       (Android WebGPU averaged 46 s a track, max 94 min). */
    var SEG_BUDGET_MS = 900, SEG_EVERY_MAX = 8;
    /* How long a click may wait for the encoder pre-flight that started when
       the clip was read; past it the export attempts the requested container
       unprobed (the encoder itself is the arbiter, and it fails fast). */
    var ENC_PROBE_WAIT_MS = 15000;
    function noteSegCost(r) {
        var ms = (r && r.via === 'seg') ? ((r.encMs | 0) + (r.decMs | 0)) : 0;
        if (ms > 0) S.seg.frameMs = S.seg.frameMs ? Math.round(S.seg.frameMs * 0.5 + ms * 0.5) : ms;
    }
    function segCadence() {
        var ms = S.seg.frameMs;
        if (!ms) return S.seg.provider === 'webgpu' ? 1 : SEG_EVERY_WASM;
        return Math.max(1, Math.min(SEG_EVERY_MAX, Math.round(ms / SEG_BUDGET_MS)));
    }
    /* Below this many frames a track stops meaning anything; the sampling rate
       is lowered only down to it. The frame budget can always hold 24 frames
       at PROC_MAX, so a refusal branch under it would be unreachable. */
    var MIN_TRACK_FRAMES = 24;
    var SCENE_MAX = 1920;        // display canvas long side (the engine renders at source size regardless)
    var MAX_OBJECTS = 5;            // the most the engine takes in one pass (Премиум)
    /* How many objects THIS visitor may mark in one pass. The plan difference is
       decided at runtime (__isProNow), never by the cached render; where the plan
       is not sold at all (__PRO_HERE === false) nobody is sold anything and the
       ceiling is the engine's. */
    function planIsPro() { try { return window.__isProNow ? !!window.__isProNow(false) : !!(window.__tierLimits && window.__tierLimits.is_pro); } catch (_) { return false; } }
    function planSoldHere() { return window.__PRO_HERE !== false; }
    function maxObjects() {
        var free = CFG.freeObjects | 0, pro = CFG.proObjects | 0;
        if (!free || !pro) return MAX_OBJECTS;
        return (planIsPro() || !planSoldHere()) ? Math.min(MAX_OBJECTS, pro) : Math.min(MAX_OBJECTS, free);
    }
    function planName() { return CFG.planName || ((window.__qualityTier && window.__qualityTier.planName) ? window.__qualityTier.planName() : ''); }
    var COLORS = ['#5b9dff', '#ff6bb5', '#ffb547', '#35d49a', '#b48cff'];

    /* ── the «Фон» stage ────────────────────────────────────────────────────
       24.09.2026, a suggestion left on this very page: «Убрать с заднего плана
       всё лишнее, сделать фон серым». Object by object that is a dozen marks
       and a dozen reconstructions of a background nobody wants back; the
       matting net /video-bg-remover runs answers it in one pass — people stay,
       everything else becomes the chosen backdrop. It is the shared BgReplace
       stage (public/js/bg-replace/bg-replace.js), and it runs AFTER the marked
       objects are rebuilt (recon-pipeline `postFrame`): a person removed with a
       tap never comes back as one of «the people», and a blur blurs the cleaned
       frame. The same session (reporter c3848581…) had already been to
       /video-bg-remover twice — the two halves of one job lived on two pages. */
    var BG_FRAME_COST = 0.25;   // matting a frame, in units of a reconstructed one (makeWorkShare)
    var BG_CAP_SLACK = 0.5;     // s — a «10 s» phone clip is 10.03 s long
    var BG_NO_PEOPLE = 0.004;   // subject share under which the preview frame has nobody in it
    function bgOn() { return !!(S.bg && S.bg.on); }
    /* The stage is /video-bg-remover's work, so it has that tool's price —
       its duration ceiling, inlined from its own tier entry (see the blade). */
    function bgCapSec() {
        var b = CFG.bg || {}, f = +b.freeSec || 0, p = +b.proSec || 0;
        if (!f || !p) return Infinity;
        return (planIsPro() || !planSoldHere()) ? p : f;
    }
    function bgOverCap() { return bgOn() && S.dur > bgCapSec() + BG_CAP_SLACK; }
    function bgWorks() { return bgOn() && !bgOverCap(); }
    /* Something to render: an object to remove, or a background to replace. */
    function hasWork() { return readyObjects().length > 0 || bgWorks(); }

    /* A phone whose binding limit is MEMORY, not speed. iOS kills the tab
       (jetsam) long before a desktop would even slow down, and every iOS browser
       is WebKit, which gets the plain-WASM segmenter: 1216 MB at peak on WebKit
       26.4 (device-capabilities.js, 08.09.2026), never handed back while its
       worker lives. Prod 09.09–14.09 (RU): 22 iPhone renders started, 3 files
       delivered, ≥9 tabs reloaded in the middle of the render. On these devices
       the page gives memory back before a render and does not run the optional
       estimate at all. */
    function tightMemory() {
        if (window.__RO_TIGHT != null) return !!window.__RO_TIGHT;
        try {
            var dc = window.__deviceCap;
            if (!dc) return false;
            var c = dc.get();
            if (c.isWebKit && (c.isMobile || c.isIOS)) return true;
            return !!(c.isMobile && c.budgetGB && c.budgetGB <= 4);
        } catch (_) { return false; }
    }

    var S = {
        file: null, url: null, video: null,
        origFile: null,          // what the user picked — cap key and output name
        source: null,            // RoSource result; `file` may be a proxy built from origFile
        vw: 0, vh: 0, dur: 0, fps: 30,
        pw: 0, ph: 0,
        worker: null, wid: 0, wpending: new Map(),
        session: null,
        objects: [],             // [{id, n, color, status, sel, region, lost…}]
        undo: [],                // [{type:'add'|'remove'|'refine', obj, …}]
        active: -1,
        nextN: 1,
        framesReady: false, restless: 0, pendingTap: null,
        samples: [], sampleFps: TRACK_FPS,
        seg: { state: 'idle', provider: null, promise: null, retried: false },
        mode: 'new',             // what the next tap on the stage means: new | add | exclude
        drag: null, busy: false,
        resultBlob: null, resultName: '', resultUrl: null,
        exportAbort: null, exporting: false,
        coverage: null, coverageFinal: false,
        compare: null, compareSeq: 0,
        state: 'empty',
        t: {},
        srcHasAudio: null,
        playing: false,
        // «Фон»: the choice (id is a BgReplace.PALETTE id or 'custom'), the stage,
        // the model's state, the last preview's frame + matte (a swatch change
        // recomposes it instantly), the subject share it showed.
        bg: { on: false, id: 'gray', custom: '#3b82f6', strength: 5 },
        bgStage: null, bgModel: 'idle', bgPrev: null, bgShare: null, bgErr: null
    };
    try { window.__roState = S; } catch (_) { }
    // Read-only views the benches were written against when there was one
    // object: `region` is the first marked object's region, `sel` the active
    // object's selection.
    try {
        Object.defineProperty(S, 'region', { get: function () { var o = S.objects.filter(function (x) { return x.status === 'ready' && x.region; })[0]; return o ? o.region : null; } });
        Object.defineProperty(S, 'sel', { get: function () { var o = S.objects[S.active]; return o ? o.sel : null; } });
    } catch (_) { }

    /* Encoder capabilities, probed once per file. Safari 16.4–18.7 ship
       WebCodecs VIDEO but no AudioEncoder; the video side stays usable and the
       sound is muxed back from the source afterwards. */
    var ENC = { probed: false, mp4: false, webm: false, audio: false, audioMp4: false, audioWebm: false };

    /* ── canvas plumbing ─────────────────────────────────────────────────── */
    var scene, sctx, after, actx, ui, uctx, sampler, samctx, timeline;

    function displayDims() {
        var sc = Math.min(1, SCENE_MAX / Math.max(S.vw, S.vh));
        return { w: Math.max(2, Math.round(S.vw * sc)), h: Math.max(2, Math.round(S.vh * sc)) };
    }
    function fitCanvas() {
        if (!S.vw) return;
        var d = displayDims();
        [scene, after, ui].forEach(function (c) { if (c.width !== d.w) c.width = d.w; if (c.height !== d.h) c.height = d.h; });
    }

    /* ── worker ──────────────────────────────────────────────────────────── */
    /* A cancelled wait is an ANSWER, not silence: «Другой файл» used to clear
       the pending map without rejecting, leaving pipelines hanging, and the
       next step called postMessage on null (Sentry GB, Android). */
    function abortedError() {
        try { return new DOMException('Aborted', 'AbortError'); }
        catch (_) { var e = new Error('Aborted'); e.name = 'AbortError'; return e; }
    }
    function isAbort(e) { return !!(e && (e.name === 'AbortError')); }

    function wcall(msg, transfer) {
        return new Promise(function (resolve, reject) {
            if (!S.worker) { reject(abortedError()); return; }
            var id = ++S.wid;
            S.wpending.set(id, { resolve: resolve, reject: reject });
            msg.id = id;
            try { S.worker.postMessage(msg, transfer || []); }
            catch (e) { S.wpending.delete(id); reject(isAbort(e) ? e : abortedError()); }
        });
    }

    var trackProgressCb = null;
    function ensureWorker() {
        if (S.worker) return Promise.resolve();
        S.worker = new Worker(CFG.workerUrl);
        S.worker.onmessage = function (ev) {
            var m = ev.data;
            if (m.type === 'trackProgress') { if (trackProgressCb) trackProgressCb(m.p); return; }
            if (m.type === 'segVerdict') { onSegVerdict(m.verdict); return; }
            if (m.type === 'cropRequest') { serveCrop(m); return; }
            var p = S.wpending.get(m.id);
            if (!p) return;
            S.wpending.delete(m.id);
            if (m.type === 'error') p.reject(new Error(m.message)); else p.resolve(m);
        };
        S.worker.onerror = function (e) {
            // A worker that dies silently looks exactly like a slow one. Say so.
            toast(T.err_engine || 'Engine failed to start.', 'error');
            ae('tool_error', { error_type: 'ro_worker_error', msg: (e && e.message) || 'worker error' });
        };
        return wcall({ type: 'init', urls: CFG.engineUrls });
    }

    /* ── native-resolution crops for the segmenter (the crop lane) ─────────
       The selection worker holds the clip at the analysis grid (480 px long
       side). A small object — a person at 5 % of the frame — is 24 px there,
       and the segmenter answers with a part of it (see ro-worker.js, the crop
       lane). So for a small box the worker asks the PAGE for the same region
       at native resolution: a hidden video element seeks to the sample's time
       and the region is drawn scaled to the requested size. One seek per
       keyframe. The display video is never touched — the user is looking at
       it. Any failure answers with no crop; the worker keeps its grid answer. */
    var cropVideo = null, cropVideoReady = null, cropCanvas = null, cropCtx = null, cropChain = Promise.resolve();
    function ensureCropVideo() {
        if (cropVideoReady) return cropVideoReady;
        cropVideoReady = new Promise(function (resolve, reject) {
            try {
                var v = document.createElement('video');
                v.muted = true; v.playsInline = true; v.preload = 'auto';
                var VFS = window.VbrFrameSource;
                if (VFS && VFS.attachHidden) VFS.attachHidden(v);
                v.src = S.url;
                var done = false;
                var timer = setTimeout(function () { if (!done) { done = true; reject(new Error('crop-video:load-timeout')); } }, 20000);
                v.addEventListener('loadeddata', function () { if (!done) { done = true; clearTimeout(timer); cropVideo = v; resolve(v); } }, { once: true });
                v.addEventListener('error', function () { if (!done) { done = true; clearTimeout(timer); reject(new Error('crop-video:error')); } }, { once: true });
                if (v.readyState >= 2) { done = true; clearTimeout(timer); cropVideo = v; resolve(v); }
            } catch (e) { reject(e); }
        });
        cropVideoReady.catch(function () { cropVideoReady = null; });
        return cropVideoReady;
    }
    function dropCropVideo() {
        if (cropVideo) {
            try { cropVideo.removeAttribute('src'); cropVideo.load(); } catch (_) { }
            try { window.VbrFrameSource && window.VbrFrameSource.detachHidden && window.VbrFrameSource.detachHidden(cropVideo); } catch (_) { }
        }
        cropVideo = null; cropVideoReady = null;
    }
    function seekCropVideo(v, t) {
        return new Promise(function (resolve) {
            var target = Math.max(0, Math.min(S.dur - 1e-3, t));
            if (Math.abs(v.currentTime - target) < 1e-3 && v.readyState >= 2) { resolve(); return; }
            var done = false;
            var fin = function () { if (done) return; done = true; v.removeEventListener('seeked', fin); resolve(); };
            v.addEventListener('seeked', fin);
            setTimeout(fin, 2500);
            try { v.currentTime = target; } catch (e) { fin(); }
        });
    }
    function serveCrop(m) {
        var reply = function (rgba, w, h) {
            try {
                if (rgba) S.worker.postMessage({ type: 'cropReply', cid: m.cid, rgba: rgba.buffer, w: w, h: h }, [rgba.buffer]);
                else S.worker.postMessage({ type: 'cropReply', cid: m.cid, rgba: null });
            } catch (_) { }
        };
        var job = function () {
            return ensureCropVideo().then(function (v) {
                return seekCropVideo(v, m.t).then(function () {
                    var kx = S.vw / S.pw, ky = S.vh / S.ph;
                    var sx = m.x * kx, sy = m.y * ky, sw = m.w * kx, sh = m.h * ky;
                    var size = Math.max(64, Math.min(1024, m.size | 0 || 512));
                    var ow = Math.round(size * (m.w >= m.h ? 1 : m.w / m.h)), oh = Math.round(size * (m.h >= m.w ? 1 : m.h / m.w));
                    if (!cropCanvas) { cropCanvas = document.createElement('canvas'); cropCtx = cropCanvas.getContext('2d', { willReadFrequently: true }); }
                    cropCanvas.width = ow; cropCanvas.height = oh;
                    cropCtx.drawImage(v, sx, sy, sw, sh, 0, 0, ow, oh);
                    var data = cropCtx.getImageData(0, 0, ow, oh).data;
                    reply(new Uint8ClampedArray(data), ow, oh);
                });
            }).catch(function (e) {
                feat('ro_crop_failed', { msg: String(e && e.message || e).slice(0, 80) });
                reply(null);
            });
        };
        cropChain = cropChain.then(job, job);
    }

    /* ── seeking the display video ───────────────────────────────────────── */
    var seekChain = Promise.resolve();
    function seekTo(t) {
        var v = S.video;
        var run = function () {
            return new Promise(function (resolve) {
                if (!v || !S.dur) { resolve(); return; }
                var target = Math.max(0, Math.min(S.dur - 1e-3, t));
                if (Math.abs(v.currentTime - target) < 1e-3 && v.readyState >= 2) { resolve(); return; }
                var done = false;
                var fin = function () { if (done) return; done = true; v.removeEventListener('seeked', fin); resolve(); };
                v.addEventListener('seeked', fin);
                setTimeout(fin, 2500);
                try { v.currentTime = target; } catch (e) { fin(); }
            });
        };
        seekChain = seekChain.then(run, run);
        return seekChain;
    }
    /* Scrubbing asks for a seek on every pointer move; only the LATEST target
       matters, and a queue of forty stale seeks is how a timeline feels dead. */
    var scrubWanted = null, scrubRunning = false;
    function scrubTo(t) {
        scrubWanted = t;
        if (scrubRunning) return;
        scrubRunning = true;
        (function next() {
            var tt = scrubWanted; scrubWanted = null;
            seekTo(tt).then(function () {
                drawScene();
                if (scrubWanted != null) next(); else scrubRunning = false;
            });
        })();
    }

    async function grab(t) {
        await seekTo(t);
        samctx.drawImage(S.video, 0, 0, S.pw, S.ph);
        return samctx.getImageData(0, 0, S.pw, S.ph).data;
    }

    function sampleIndexAt(t) {
        var best = 0, bd = 1e9;
        for (var i = 0; i < S.samples.length; i++) { var d = Math.abs(S.samples[i] - t); if (d < bd) { bd = d; best = i; } }
        return best;
    }

    /* ── read the clip once ──────────────────────────────────────────────── */
    /* The ceiling is the SUM of frames, not the size of one: they live in the
       worker together, and a ten-minute clip at five per second is 1.5 GB —
       where `new Uint8ClampedArray` answered "Array buffer allocation failed"
       (Sentry G9). The budget lowers the sampling RATE instead of refusing. */
    /* An even spread of ≤ max sample frames, copied (the originals are about to
       be transferred to the selection worker). ~0.5 MB each at proc res. */
    function pickPlateSamples(frames, max) {
        var n = frames.length, take = Math.min(n, Math.max(1, max | 0)), out = [];
        for (var i = 0; i < take; i++) {
            var k = take === 1 ? 0 : Math.round(i * (n - 1) / (take - 1));
            var src = frames[k];
            if (!src || !src.buf) continue;
            out.push({ rgba: new Uint8ClampedArray(src.buf.slice(0)), t: src.t });
        }
        return out;
    }

    async function readSamples() {
        var tRead = performance.now();
        var dm = (typeof navigator !== 'undefined' && navigator.deviceMemory) || 4;
        var budgetPx = window.__RO_BUDGET_PX || (dm <= 2 ? 16000000 : (dm <= 4 ? 40000000 : 120000000));
        var perFrame = Math.max(1, S.pw * S.ph);
        var maxFrames = Math.max(MIN_TRACK_FRAMES, Math.floor(budgetPx / perFrame));
        var fps = TRACK_FPS;
        var wantFrames = Math.ceil(S.dur * fps) || 1;
        if (wantFrames > maxFrames) {
            fps = Math.max(0.5, maxFrames / Math.max(0.1, S.dur));
            feat('ro_track_downsampled', { dur: Math.round(S.dur), want: wantFrames, max: maxFrames, dm: dm });
        }
        S.sampleFps = fps;
        try {
            window.__roTrackPlan = {
                frames: Math.ceil(S.dur * fps), step: Math.round(S.fps / fps), perFramePx: perFrame,
                budgetPx: budgetPx, maxFrames: maxFrames, downsampled: wantFrames > maxFrames,
                heldMb: Math.round(Math.ceil(S.dur * fps) * perFrame * 4 / 1048576)
            };
        } catch (_) { }
        var frames = [], transfer = [], times = [], method = 'seek';
        var thumbs = !S.thumbsDone;   // a re-read after a render keeps the strip it already has
        var ac = new AbortController(); S.readAbort = ac;
        var N = Math.max(1, Math.round(S.dur * fps));
        setBusy(T.reading || 'Reading the clip…', 0);
        async function onFrame(canvas, idx, tSec) {
            var d = canvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, S.pw, S.ph).data;
            var c = new Uint8ClampedArray(d);
            frames.push({ buf: c.buffer, t: tSec }); transfer.push(c.buffer); times.push(tSec);
            if (timeline && thumbs) timeline.addThumb(tSec, canvas);
            if ((idx & 3) === 0) setBusy(T.reading || 'Reading the clip…', idx / N);
        }
        try {
            if (!window.VbrFrameSource) throw new Error('frame-source missing');
            var res = await window.VbrFrameSource.iterate({
                file: S.file, url: S.url, fps: fps, trimStart: 0, trimEnd: S.dur,
                width: S.pw, height: S.ph, abortSignal: ac.signal,
                // The floor below seeks the display video, which is on the page
                // and therefore decoded on iOS; the frame source's own floor
                // is a second element iOS may park (see frame-source.js).
                noSeekFallback: true
            }, onFrame);
            method = (res && res.method) || 'unknown';
        } catch (e) {
            if (ac.signal.aborted || isAbort(e)) throw e;
            /* RESUME, never restart: WebKit's decoder is reclaimed mid-clip under
               memory pressure («The object is in an invalid state», resume_at:1
               on iPhone), and the frames already read are good. */
            var resume = (e && e.resumeAt != null) ? Math.min(frames.length, e.resumeAt | 0) : 0;
            feat('ro_samples_fallback', { msg: String(e && e.message || e).slice(0, 100), resume: resume });
            frames = frames.slice(0, resume); transfer = transfer.slice(0, resume); times = times.slice(0, resume);
            method = resume ? 'webcodecs+seek' : 'seek-inline';
            S.inlineSeek = true;
            for (var k = resume; k * (1 / fps) < S.dur; k++) {
                if (ac.signal.aborted) throw abortedError();
                var t = k / fps;
                var data = await grab(t);
                var cc = new Uint8ClampedArray(data);
                frames.push({ buf: cc.buffer, t: t }); transfer.push(cc.buffer); times.push(t);
                if (timeline && thumbs) timeline.addThumb(t, sampler);
                setBusy(T.reading || 'Reading the clip…', k / N);
            }
            S.inlineSeek = false;
        }
        S.readAbort = null;
        if (!frames.length) throw new Error('no frames');
        // The long-reach sources of the fill (recon-worker longReachSources):
        // a spread of these very frames, copied BEFORE the buffers move to the
        // selection worker. The page keeps them so every reconstruction session
        // — the probe's, the preview's, the render's, the one rebuilt after a
        // memory release — gets the same set. Tight devices keep fewer.
        S.plateSamples = pickPlateSamples(frames, tightMemory() ? 16 : 40);
        if (S.session && S.session.setSamples && S.plateSamples) S.session.setSamples(S.plateSamples);
        await ensureWorker();
        await wcall({ type: 'frames', frames: frames, w: S.pw, h: S.ph, cropLane: window.__RO_CROP_LANE !== false }, transfer);
        S.samples = times;
        S.framesReady = true;
        S.thumbsDone = true;
        S.t.read = Math.round(performance.now() - tRead);
        feat('ro_samples', { n: times.length, fps: +fps.toFixed(2), ms: S.t.read, method: method, dm: dm });
        stage('scene_ready');
    }

    /* The legacy scene statistic (share of handheld footage), kept for
       telemetry continuity — built only when the worker is idle. */
    function plateStatLater() {
        if (S.restless || !S.framesReady) return;
        wcall({ type: 'plate' }).then(function (r) {
            S.restless = r.restless || 0;
            feat('ro_plate_built', { restless: Math.round(S.restless * 100), ms: 0 });
        }).catch(function () { });
    }

    /* ── segmenter ───────────────────────────────────────────────────────── */
    /* The provider's verdict from the worker's known-answer test. Only a WRONG
       ANSWER is remembered (30 days, this adapter + this model) — a throw is a
       bad day, not a verdict — and the memory lives here because a worker has
       no localStorage. Same shape as RifeCore. */
    var segVerdictKey = null;
    function segVerdictKeyFor(adapter) {
        var V = window.OrtEpVerify;
        var model = (CFG.seg && CFG.seg.model && CFG.seg.model.webgpu && CFG.seg.model.webgpu.enc) || 'edgetam';
        var parts = ['edgetam-1', model.split('/').pop().split('?')[0], (adapter && (adapter.vendor + '/' + adapter.architecture)) || 'gpu?', navigator.userAgent.slice(0, 40)];
        return V && V.verdictKey ? V.verdictKey(parts) : parts.join('|');
    }
    function onSegVerdict(v) {
        var V = window.OrtEpVerify;
        try {
            if (V && segVerdictKey && v && !v.ok && !v.error && !v.skipped && v.provider !== 'wasm') V.remember(segVerdictKey, v.provider, { kind: 'edgetam-kat', iou: v.iou });
            feat('ro_seg_verdict', Object.assign({ iou: v && v.iou != null ? v.iou : null, area: v && v.area != null ? v.area : null },
                V && V.telemetryOf ? V.telemetryOf(v, S.seg.adapter) : { ep: v && v.provider, ok: v && v.ok ? 1 : 0 }));
        } catch (_) { }
    }
    function segInit() {
        if (S.seg.promise) return S.seg.promise;
        if (!CFG.seg) { S.seg.state = 'off'; return Promise.resolve({ ok: false, reason: 'no-config' }); }
        S.seg.state = 'loading';
        var t0 = performance.now();
        S.seg.promise = ensureWorker().then(function () {
            // Which ONNX Runtime build this browser gets is decided ONCE, by
            // window.__ortBuild: WebKit gets the plain WASM build (the JSEP one
            // is onnxruntime#26827 — the tab is killed).
            var ob = window.__ortBuild;
            if (!ob || typeof ob.url !== 'function') { S.seg.state = 'off'; return Promise.resolve({ ok: false, reason: 'no-ort-owner' }); }
            var eps = (typeof ob.wasmOnly === 'function' && ob.wasmOnly()) ? ['wasm'] : ['webgpu', 'wasm'];
            var V = window.OrtEpVerify;
            var adapterP = (V && V.adapterIdentity && eps[0] === 'webgpu') ? V.adapterIdentity() : Promise.resolve(null);
            return adapterP.then(function (adapter) {
                S.seg.adapter = adapter;
                segVerdictKey = segVerdictKeyFor(adapter);
                var rejected = {};
                if (V && V.isRejected) eps.forEach(function (ep) { if (ep !== 'wasm' && V.isRejected(segVerdictKey, ep)) rejected[ep] = 1; });
                if (rejected.webgpu) { S.seg.rejectedAtInit = true; feat('ro_seg_skip', { ep: 'webgpu', why: 'cached-rejection' }); }
                return wcall({ type: 'segInit', ortUrl: ob.url(), ortFallback: typeof ob.fallback === 'function' ? ob.fallback() : null,
                    wasmBase: typeof ob.wasmBase === 'function' ? ob.wasmBase() : null, model: CFG.seg.model, eps: eps, rejected: rejected,
                    katFail: window.__RO_KAT_FAIL || null, fakeWrong: window.__RO_FAKE_WRONG | 0 });
            });
        }).then(function (r) {
            S.seg.state = r.ok ? 'ready' : 'failed';
            S.seg.provider = r.provider || null;
            feat('ro_seg_ready', { ok: r.ok ? 1 : 0, provider: r.provider || '', ms: r.ms || Math.round(performance.now() - t0), dl_ms: r.dlMs || 0, mb: r.bytes ? +(r.bytes / 1048576).toFixed(1) : 0, reason: r.reason || '', cached: r.cached ? 1 : 0,
                verified: r.verified || 0, unverified: r.unverified || 0, kat_iou: r.kat_iou, kat_ms: r.kat_ms, rungs: r.rungs || 0, errs: r.errs || '',
                threads: r.threads || 1, coi: (typeof crossOriginIsolated !== 'undefined' && crossOriginIsolated) ? 1 : 0 });
            // A step-down is not an implementation detail to the person waiting:
            // the tool is about to be seconds-per-tap instead of instant, and
            // the reason is their device, not their file. Say it once, durably.
            if (r.ok && r.provider === 'wasm' && (S.seg.rejectedAtInit || (r.rungs || 0) > 1)) setSegNote('stepped-down');
            else if (!r.ok) setSegNote('unavailable');
            return r;
        }).catch(function (e) {
            S.seg.state = 'failed';
            feat('ro_seg_ready', { ok: 0, reason: String(e && e.message || e).slice(0, 120), ms: Math.round(performance.now() - t0) });
            return { ok: false, reason: String(e && e.message || e) };
        });
        return S.seg.promise;
    }

    /* ── the answer itself is evidence ────────────────────────────────────
     *
     * A mask that swallows the frame is not a verdict about the user's aim: it
     * is the model saying something impossible. Until 16.09.2026 the tool read
     * it as the former and answered «almost the whole frame is under your tap —
     * tap more precisely», which on a device whose GPU computes the model wrong
     * is an accusation, and a dead end: the box the message offers instead came
     * back the same way. A buyer on a Galaxy S22 (Adreno 730, Yandex 26.80) got
     * that message five times, bought Premium expecting it to lift a limit, and
     * asked for a refund twenty minutes later.
     *
     * So the first such answer sends the provider back to its own known-answer
     * test (the worker's `segRecheck`, thresholds measured there). If the
     * provider fails it, the page remembers the rejection, the segmenter is
     * rebuilt one rung down, the selection is retried for the user, and the
     * note below says what happened. If it passes, nothing changes and the
     * original message stands — the tap really was on the background. */
    function wrongAnswer(r) {
        return !!r && r.via === 'seg' && r.reason === 'blob-too-large';
    }
    /* True once this device's segmenter has been caught giving a wrong answer:
       from here on the tool owns the failure instead of blaming the tap. */
    function segBlamesItself() { return !!S.seg.selfRejected; }

    function recheckSegAfterWrongAnswer() {
        if (S.seg.recheckDone || S.seg.state !== 'ready') return Promise.resolve(false);
        S.seg.recheckDone = true;
        var was = S.seg.provider;
        var t0 = performance.now();
        return wcall({ type: 'segRecheck' }).then(function (r) {
            feat('ro_seg_recheck', { was: was || '', now: r.provider || '', kept: r.kept ? 1 : 0, ok: r.ok ? 1 : 0,
                iou: r.iou != null ? r.iou : null, ms: Math.round(performance.now() - t0) });
            if (r.kept) return false;
            S.seg.selfRejected = true;
            S.seg.provider = r.provider || null;
            S.seg.state = r.ok ? 'ready' : 'failed';
            setSegNote(r.ok ? 'stepped-down' : 'unavailable');
            return !!r.ok;
        }).catch(function () { return false; });
    }

    /* The note is durable and sits with the tool, not in a toast that leaves:
       the person needs it while they work, the same rule FRLandmarks follows
       for a detector that could not be built. */
    function setSegNote(kind) {
        var el = $('roSegNote');
        if (!el) return;
        var txt = kind === 'stepped-down' ? T.seg_gpu_rejected_note : T.seg_unavailable_note;
        if (!txt) return;
        el.textContent = txt;
        el.hidden = false;
        try { window.__announce && window.__announce(txt); } catch (_) { }
    }

    /* The tap must not race the model: a selection made while the weights are
       still downloading would silently take the arithmetic fallback. One retry
       after a failed load (a first fetch was seen aborted mid-body). */
    async function segWait() {
        if (S.seg.state === 'failed' && !S.seg.retried) {
            S.seg.retried = true; S.seg.promise = null; S.seg.state = 'idle';
            feat('ro_seg_retry');
        }
        if (S.seg.state === 'ready' || S.seg.state === 'failed' || S.seg.state === 'off') return S.seg.state === 'ready';
        setBusy(T.loading_model || 'Loading the object model…', null);
        var r = await Promise.race([segInit(), new Promise(function (res) { setTimeout(function () { res({ ok: false, reason: 'timeout' }); }, 90000); })]);
        return !!(r && r.ok);
    }

    /* ── objects ─────────────────────────────────────────────────────────── */
    function activeObj() { return S.objects[S.active] || null; }
    function readyObjects() { return S.objects.filter(function (o) { return o.status === 'ready' && o.region; }); }
    function readyRegions() { return readyObjects().map(function (o) { return o.region; }); }
    function objName(o) { return tr('obj_name', { n: o.n }, 'Object ' + o.n); }

    function newObject() {
        var used = S.objects.map(function (o) { return o.color; });
        var color = COLORS.filter(function (c) { return used.indexOf(c) < 0; })[0] || COLORS[S.nextN % COLORS.length];
        var o = { id: 'obj' + S.nextN, n: S.nextN, color: color, status: 'selecting', sel: null, region: null, lost: null };
        S.nextN++;
        S.objects.push(o);
        S.active = S.objects.length - 1;
        return o;
    }
    /* Undo: a mark, a refinement or a removal. Three tracks a session is the
       prod average, and a refinement that makes the selection worse used to
       cost a fresh tap plus a fresh track to get back to where it was. */
    function pushUndo(entry) {
        S.undo.push(entry);
        if (S.undo.length > 20) S.undo.shift();
    }
    function undo() {
        if (S.exporting || S.working) return;
        var e = S.undo.pop();
        if (!e) return;
        if (e.type === 'add') {
            var k = S.objects.indexOf(e.obj);
            if (k >= 0) S.objects.splice(k, 1);
            S.active = Math.min(S.active, S.objects.length - 1);
        } else if (e.type === 'remove') {
            S.objects.splice(Math.min(e.index, S.objects.length), 0, e.obj);
            S.active = S.objects.indexOf(e.obj);
        } else if (e.type === 'refine') {
            e.obj.sel = e.prev.sel; e.obj.region = e.prev.region; e.obj.lost = e.prev.lost; e.obj.status = 'ready';
            S.active = S.objects.indexOf(e.obj);
        }
        if (!S.objects.length) setMode('new');
        feat('ro_undo', { type: e.type, left: S.undo.length });
        hideCompare();
        onObjectsChanged({});
    }

    function removeObject(i) {
        var o = S.objects[i];
        if (!o || S.exporting) return;
        pushUndo({ type: 'remove', obj: o, index: i });
        S.objects.splice(i, 1);
        if (S.active >= S.objects.length) S.active = S.objects.length - 1;
        if (!S.objects.length) setMode('new');
        feat('ro_obj_remove', { left: S.objects.length });
        hideCompare();
        onObjectsChanged();
    }

    function selectionFrom(r, index, points, box) {
        var mask = r.mask ? new Uint8Array(r.mask) : null;
        return { index: index, points: points || [], box: box || null, mask: mask, bbox: r.bbox || null, area: r.area || 0, via: r.via, conf: r.confidence || 0 };
    }

    function boxAt(r, t) {
        if (!r) return null;
        var kf = r.keyframes;
        if (!kf || !kf.length) return { x: r.x, y: r.y, w: r.w, h: r.h };
        if (t <= kf[0].t) return kf[0];
        if (t >= kf[kf.length - 1].t) return kf[kf.length - 1];
        for (var i = 1; i < kf.length; i++) {
            if (t <= kf[i].t) {
                var a = kf[i - 1], b = kf[i];
                var f = (t - a.t) / Math.max(1e-6, b.t - a.t);
                return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, w: a.w + (b.w - a.w) * f, h: a.h + (b.h - a.h) * f };
            }
        }
        return kf[kf.length - 1];
    }

    /* Which marked object is under this point at this time — a tap on an
       object that is already marked makes it the active one instead of
       marking it twice. */
    function objectAt(px, py, t) {
        if (!window.RtMaskBuilder) return -1;
        for (var i = S.objects.length - 1; i >= 0; i--) {
            var o = S.objects[i];
            if (!o.region || t < o.region.startTime - 0.05 || t > o.region.endTime + 0.05) continue;
            var sil = silhouetteOf(o, t);
            if (sil && sil.core[Math.round(py) * S.pw + Math.round(px)]) return i;
        }
        return -1;
    }

    async function tapAt(px, py) {
        // Tapping before the clip has been read used to do nothing at all — a
        // dead click during the one window where the page most looks ready.
        if (!S.framesReady) {
            S.pendingTap = { x: px, y: py, mode: S.mode };
            S.pendingBox = null;
            setPill(T.pill_queued || '', 'ok');
            feat('ro_tap_queued', { restore: S.analysisReleased ? 1 : 0 });
            // After a render on a phone the clip's frames were handed back
            // (releaseForRender); the tap is what brings them back.
            if (S.analysisReleased) restoreAnalysis();
            return;
        }
        var t = S.video.currentTime;
        var idx = sampleIndexAt(t);
        var mode = S.mode;
        var target = activeObj();
        if (mode === 'new') {
            var hit = objectAt(px, py, t);
            if (hit >= 0) { S.active = hit; renderRail(); drawScene(); feat('ro_obj_focus'); return; }
            if (readyObjects().length >= maxObjects()) { objectCapReached('tap'); return; }
        } else if (!target || !target.region) {
            setMode('new');
            mode = 'new';
        }

        var obj, points, box = null, label = mode === 'exclude' ? 0 : 1;
        if (mode === 'new') {
            obj = newObject();
            points = [{ x: px, y: py, label: 1 }];
        } else {
            obj = target;
            if (obj.sel && obj.sel.index === idx) {
                points = obj.sel.points.concat([{ x: px, y: py, label: label }]);
                box = obj.sel.box;
            } else {
                // Refining on another frame: re-prompt there with the object's
                // own box and centre, plus the new point. The model has no
                // memory of the first frame's taps, so the object has to be
                // restated before the correction means anything.
                var b = boxAt(obj.region, t);
                var bx = { x: b.x * S.pw, y: b.y * S.ph, w: b.w * S.pw, h: b.h * S.ph };
                points = [{ x: bx.x + bx.w / 2, y: bx.y + bx.h / 2, label: 1 }, { x: px, y: py, label: label }];
                if (mode === 'add') {
                    var x0 = Math.min(bx.x, px - 4), y0 = Math.min(bx.y, py - 4);
                    var x1 = Math.max(bx.x + bx.w, px + 4), y1 = Math.max(bx.y + bx.h, py + 4);
                    box = { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
                } else box = bx;
            }
        }
        obj.status = 'selecting';
        setWorking(true);
        renderRail();
        var hadModel = await segWait();
        setBusy(T.finding || 'Finding the object…', null);
        setPill(T.pill_finding || '', 'busy');
        var t0 = performance.now();
        var r;
        try {
            r = await wcall({ type: 'select', index: idx, points: points, box: box });
        } catch (e) {
            setWorking(false);
            if (isAbort(e)) return;
            throw e;
        }
        noteSegCost(r);
        try { window.__roLastSelect = { reason: r.reason, bbox: r.bbox, area: r.area, iou: r.iou, obj: r.obj, crop: r.crop, rect: r.rect, cands: r.cands, passes: r.passes }; } catch (_) { }   // stands read this
        var retried = 0;
        if (wrongAnswer(r)) {
            var again = await recheckSegAfterWrongAnswer();
            if (again) {
                retried = 1;
                try { r = await wcall({ type: 'select', index: idx, points: points, box: box }); }
                catch (e2) { setWorking(false); if (isAbort(e2)) return; throw e2; }
                noteSegCost(r);
            }
        }
        feat('ro_tap', { conf: Math.round((r.confidence || 0) * 100), reason: r.reason, via: r.via || '', ms: Math.round(performance.now() - t0), enc_ms: r.encMs || 0, dec_ms: r.decMs || 0, pts: points.length, model: hadModel ? 1 : 0, mode: mode, objs: S.objects.length, retry: retried, crop: r.crop ? 1 : 0 });

        if (!r.mask || !r.bbox || r.confidence < 0.55) {
            // Declining is a feature: a mask we cannot stand behind erases
            // background and leaves the object. Say what happened, and say
            // that a box is the other way in.
            var msg;
            if (r.reason === 'blob-too-large') msg = segBlamesItself() ? T.pill_model_wrong : T.pill_too_big;
            else if (r.via === 'seg') msg = T.pill_none;
            else msg = T.pill_declined;
            feat('ro_declined', { reason: r.reason, via: r.via || '', dur: Math.round(S.dur), mode: mode });
            if (mode === 'new') { S.objects.splice(S.objects.indexOf(obj), 1); S.active = S.objects.length - 1; S.nextN = Math.max(1, S.nextN - 1); }
            else obj.status = obj.region ? 'ready' : 'failed';
            setWorking(false);
            renderRail();
            setPill(msg || '', 'warn', true);
            return;
        }
        if (mode !== 'new' && obj.region) obj._prev = { sel: obj.sel, region: obj.region, lost: obj.lost };
        obj.sel = selectionFrom(r, idx, points, box);
        drawScene();
        await trackObject(obj);
    }

    /* NB: not `boxAt` — a second function of the same name in this scope
       silently replaced the first once already (a drawn box did nothing, no
       error anywhere; node --check is blind to it). */
    async function boxDrawn(bx, by, bw, bh) {
        if (!S.framesReady) {
            if (S.analysisReleased) { S.pendingBox = { x: bx, y: by, w: bw, h: bh }; S.pendingTap = null; setPill(T.pill_queued || '', 'ok'); restoreAnalysis(); }
            return;
        }
        if (readyObjects().length >= maxObjects()) { objectCapReached('box'); return; }
        var idx = sampleIndexAt(S.video.currentTime);
        var box = { x: bx, y: by, w: bw, h: bh };
        feat('ro_box_drawn');
        var obj = newObject();
        setWorking(true);
        renderRail();
        var hadModel = await segWait();
        setBusy(T.finding || 'Finding the object…', null);
        setPill(T.pill_finding || '', 'busy');
        var r;
        try { r = await wcall({ type: 'select', index: idx, points: [], box: box }); }
        catch (e) { setWorking(false); if (isAbort(e)) return; throw e; }
        if (wrongAnswer(r)) {
            var againB = await recheckSegAfterWrongAnswer();
            if (againB) {
                try { r = await wcall({ type: 'select', index: idx, points: [], box: box }); }
                catch (e3) { setWorking(false); if (isAbort(e3)) return; throw e3; }
            }
        }
        try { window.__roLastSelect = { reason: r.reason, bbox: r.bbox, area: r.area, iou: r.iou, obj: r.obj, crop: r.crop, rect: r.rect, cands: r.cands, passes: r.passes }; } catch (_) { }   // stands read this
        // A drawn box is the user's geometry: trusted as the seed even when the
        // segmenter has nothing to add (no model, or a mask that spilled out).
        obj.sel = (r.mask && r.bbox && r.confidence >= 0.5) ? selectionFrom(r, idx, [], box) : { index: idx, points: [], box: box, mask: null, bbox: box, area: bw * bh, via: r.via, conf: 0 };
        noteSegCost(r);
        feat('ro_box', { conf: Math.round((r.confidence || 0) * 100), reason: r.reason, via: r.via || '', model: hadModel ? 1 : 0, mask: obj.sel.mask ? 1 : 0, enc_ms: r.encMs || 0, dec_ms: r.decMs || 0 });
        drawScene();
        await trackObject(obj);
    }

    function fmtTime(s) {
        s = Math.max(0, s || 0);
        var m = Math.floor(s / 60), r = Math.floor(s - m * 60);
        return m + ':' + (r < 10 ? '0' : '') + r;
    }

    async function trackObject(obj) {
        var sel = obj.sel;
        if (!sel) { setWorking(false); return; }
        var tTrack = performance.now();
        obj.status = 'tracking';
        renderRail();
        setPill(tr('pill_tracking', { name: objName(obj) }), 'busy');
        setBusy(T.tracking || 'Tracking…', 0);
        trackProgressCb = function (p) { setBusy(T.tracking || 'Tracking…', p); };
        var maskCopy = sel.mask ? new Uint8Array(sel.mask) : null;
        var segEvery = segCadence();
        var res;
        try {
            res = await wcall({
                type: 'track', startIndex: sel.index,
                box: sel.box ? { x: sel.box.x, y: sel.box.y, w: sel.box.w, h: sel.box.h } : null,
                seed: sel.points.length ? { x: sel.points[0].x, y: sel.points[0].y } : null,
                mask: maskCopy ? maskCopy.buffer : null,
                segEvery: segEvery,
                debug: !!window.__RO_TRACK_DEBUG   // stands: per-sample notes in the trail
            }, maskCopy ? [maskCopy.buffer] : []);
            try { window.__roLastTrack = res; } catch (_) { }   // stands read this
        } catch (e) {
            trackProgressCb = null;
            setWorking(false);
            try { window.__roLastTrackError = (e && e.message) || String(e); } catch (_) { }   // stands read this
            if (isAbort(e)) return;
            obj.status = obj.region ? 'ready' : 'failed';
            renderRail();
            throw e;
        }
        trackProgressCb = null;

        if (!res.keyframes || !res.keyframes.length) {
            feat('ro_track_empty', { seed: res.seedMode || 'tap', reason: res.lostReason || '' });
            if (!obj.region) { S.objects.splice(S.objects.indexOf(obj), 1); S.active = S.objects.length - 1; }
            else { obj.status = 'ready'; if (obj._prev) { obj.sel = obj._prev.sel; obj._prev = null; } }
            setWorking(false);
            renderRail();
            setPill(T.pill_declined || '', 'warn', true);
            return;
        }
        // The region lives only where the object was SEEN. Holding the last box
        // over the rest of the clip after a loss erases real background where
        // the object no longer is — the sample spacing is the honest slack.
        if (obj._prev) { pushUndo({ type: 'refine', obj: obj, prev: obj._prev }); obj._prev = null; }
        else if (!obj.region) pushUndo({ type: 'add', obj: obj });
        var kfs = res.keyframes;
        var spacing = 1 / Math.max(0.1, S.sampleFps);
        var startT = 0, endT = S.dur;
        // The object can leave at BOTH ends (lost walking forward AND back from the
        // seed); each end bounds the region on its own side. `lost` names the first.
        var lostFwd = res.lostFwdT != null || (res.lost != null && res.lostDir === 'fwd');
        var lostBack = res.lostBackT != null || (res.lost != null && res.lostDir === 'back');
        if (lostFwd) endT = Math.min(S.dur, kfs[kfs.length - 1].t + spacing);
        if (lostBack) startT = Math.max(0, kfs[0].t - spacing);
        obj.region = {
            id: obj.id, method: 'reconstruct', shape: 'rect',
            x: kfs[0].x, y: kfs[0].y, w: kfs[0].w, h: kfs[0].h,
            keyframes: kfs,
            // silhouette keyframes ({t,x,y,w,h,mw,mh,rle}) — the engine cuts the
            // figure, not the rectangle; empty when no model answered
            masks: (res.masks && res.masks.length) ? res.masks : null,
            startTime: startT, endTime: endT,
            moving: true, forceWindow: true
        };
        obj.lost = res.lost != null ? { t: res.lostT || 0, dir: res.lostDir, reason: res.lostReason || '' } : null;
        obj.status = 'ready';
        S.t.track = Math.round(performance.now() - tTrack);
        stage('object_marked', { mode: res.mode, kfs: kfs.length });
        feat('ro_tracked', {
            ms: S.t.track,
            kfs: kfs.length, mode: res.mode, seed: res.seedMode || 'tap',
            plate_share: res.samples ? Math.round(res.plateSamples / res.samples * 100) : 0,
            seg: res.segCount || 0, seg_alt: res.segAlt || 0, seg_rej: res.segRejected || 0, seg_off: res.segOff ? 1 : 0, plate_n: res.plateCount || 0, seg_prov: res.segProvider || '',
            masks: res.masks ? res.masks.length : 0, seg_every: segEvery, seg_ms: S.seg.frameMs || 0,
            rej_by: res.segRejBy ? Object.keys(res.segRejBy).filter(function (k) { return res.segRejBy[k]; }).map(function (k) { return k + res.segRejBy[k]; }).join(',') : '',
            lost: res.lost != null ? 1 : 0, lost_dir: res.lostDir || '', lost_t: res.lostT != null ? Math.round(res.lostT) : -1, lost_both: (res.lostFwdT != null && res.lostBackT != null) ? 1 : 0,
            lost_reason: res.lostReason || '',
            span: Math.round((endT - startT) / Math.max(1e-3, S.dur) * 100),
            objs: readyObjects().length,
            crop: res.cropServed || 0, crop_fail: res.cropFailed || 0, crop_ms: res.cropMs || 0
        });
        setWorking(false);
        onObjectsChanged({ compareAt: S.objects.length && readyObjects().length === 1 ? sel.index : null, obj: obj });
        plateStatLater();
    }

    /* Everything that follows a change to the set of objects: the rail, the
       timeline lanes, a fresh estimate, and — for the first object — one frame
       of the real result on the stage. */
    function onObjectsChanged(o) {
        o = o || {};
        S.coverage = null; S.coverageFinal = false;
        S.bgPrev = null;             // the cached preview frame still had the old removal in it
        renderRail();
        renderTracks();
        drawScene();
        var ready = readyObjects().length;
        // The last object undone used to leave the stage saying «Объект
        // выделен…» over an empty list: the pill follows the state now.
        if (!hasWork()) { setState('select'); pillForState(); return; }
        setState('ready');
        var ob = o.obj || activeObj();
        if (ob && ob.lost) {
            setPill(tr(ob.lost.dir === 'back' ? 'pill_ready_lost_before' : 'pill_ready_lost', { name: objName(ob), t: fmtTime(ob.lost.t) }), 'warn', true);
        } else {
            setPill(readyPill(), 'ok', true);
        }
        if (!ready) return;          // background only: nothing for the estimate to measure
        if (o.compareAt != null && S.samples[o.compareAt] != null) {
            showCompare(S.samples[o.compareAt], true);
        } else {
            scheduleEstimate(250);
        }
    }

    /* ── session + the one lock around it ────────────────────────────────── */
    /* MI-GAN for the pixels no frame ever shows (RU shard only — the weights'
       licence; the blade gates the config structurally). */
    var _migan = null;
    function miganHook() {
        if (_migan !== null) return _migan || null;
        _migan = (window.RO_AI && window.RtNetFill) ? window.RtNetFill.createHook(window.RO_AI) : false;
        return _migan || null;
    }
    /* The render's ceiling on pictures per second, and the frames under it: the
       source's own, kept when at least ¾ of a 1/maxFps step from the last kept one
       (60 → every second, 50 → every second, 30 / 29.97 / 25 / 24 → all) — and
       always when the frame itself stays on screen that long (the first frame of
       a slow stretch after a fast one, the last frame of the clip). */
    var RO_MAX_FPS = 30;
    function thinFrameTimes(tt, maxFps) {
        if (!tt || !tt.length || !(maxFps > 0)) return tt;
        var gap = 0.75 / maxFps, out = [tt[0]], last = tt[0];
        for (var i = 1; i < tt.length; i++) {
            var shown = i + 1 < tt.length ? tt[i + 1] - tt[i] : Infinity;
            if (tt[i] - last >= gap || shown >= gap) { out.push(tt[i]); last = tt[i]; }
        }
        return out;
    }
    function ensureSession() {
        if (S.session) return S.session;
        S.session = window.RtReconPipeline.createSession({
            file: S.file, url: S.url, width: S.vw, height: S.vh,
            duration: S.dur, fps: S.fps, procMaxEdge: PROC_MAX,
            frameTimes: S.frameTimes || null,   // the file's own frames (see the file load)
            workerUrl: CFG.reconWorkerUrl, engineUrls: CFG.reconEngineUrls,
            netFill: miganHook()
        });
        if (S.plateSamples && S.session.setSamples) S.session.setSamples(S.plateSamples);
        return S.session;
    }

    /* The probe, the preview and the export share one worker ring and one set
       of emit waiters: two at once would answer each other's frames. Every use
       goes through this queue; anything that is not the probe cancels a running
       probe first, and waits for it to let go. */
    var sessQ = { tail: Promise.resolve(), probeAbort: null, previewAbort: null };
    function withSession(kind, fn) {
        if (kind !== 'probe' && sessQ.probeAbort) { try { sessQ.probeAbort.abort(); } catch (_) { } }
        /* A preview outranks nothing. It used to be the one job here that could
           not be stopped, and on a phone whose decoder had been reclaimed it
           waited out a video element that never loaded — with the export queued
           behind it: 7 phone sessions sat on «processing» with no render at all
           (0 on desktops), 13–15.09.2026. */
        if (kind !== 'probe' && sessQ.previewAbort) { try { sessQ.previewAbort.abort(); } catch (_) { } }
        var run = sessQ.tail.then(function () { return fn(); }, function () { return fn(); });
        sessQ.tail = run.catch(function () { });
        return run;
    }

    /* ── estimate, behind the button ─────────────────────────────────────── */
    var estimateSeq = 0, estimateTimer = 0;
    function scheduleEstimate(delay) {
        estimateSeq++;
        if (sessQ.probeAbort) { try { sessQ.probeAbort.abort(); } catch (_) { } }
        clearTimeout(estimateTimer);
        if (!readyObjects().length) return;
        /* On a phone whose limit is memory the estimate is not worth what it
           costs: it decodes nine windows of the clip next to the display video
           and the segmenter, and on iPhones it ran for minutes (prod, WebKit,
           09.09–14.09: mean 310 s) while competing with the render it describes.
           The wipe on the stage is a rendered frame — the evidence — and the done
           screen still reports the real share after the render. */
        if (tightMemory()) return;
        renderCoverage('pending');
        estimateTimer = setTimeout(runEstimate, delay == null ? 350 : delay);
    }
    function probeTimes(regs) {
        // Middle first, then the quarters, then the ends: the first instants
        // already give a usable figure, and the ends — where a figure enters or
        // leaves the frame — keep the final number honest (five mid-clip
        // instants over-promised by 13 points against the render).
        var order = [0.5, 0.2, 0.8, 0.05, 0.95, 0.35, 0.65];
        var per = Math.max(3, Math.ceil(7 / regs.length));
        var out = [];
        for (var i = 0; i < per; i++) {
            regs.forEach(function (r) {
                var a = r.startTime || 0, b = r.endTime == null ? S.dur : r.endTime;
                if (i < order.length) out.push(a + order[i] * (b - a));
            });
        }
        return out.slice(0, 9);
    }
    async function runEstimate() {
        var my = estimateSeq;
        var regs = readyRegions();
        if (!regs.length || S.exporting) return;
        var times = probeTimes(regs);
        var ac = new AbortController();
        var tCov = performance.now();
        await withSession('probe', async function () {
            if (my !== estimateSeq || S.exporting) return;
            sessQ.probeAbort = ac;
            try {
                var sess = ensureSession();
                sess.setRegions(regs);
                var cov = await sess.probeCoverage(times, {
                    signal: ac.signal,
                    onProgress: function (f, c) { if (my === estimateSeq) renderCoverage('progress', c, f); }
                });
                if (my !== estimateSeq) return;
                S.coverage = cov; S.coverageFinal = true;
                S.t.cov = Math.round(performance.now() - tCov);
                renderCoverage('final', cov);
                var pm = sess.probeMethods ? sess.probeMethods() : null;
                stage('estimated', { pct: cov ? Math.round(cov.ratio * 100) : -1 });
                feat('ro_coverage', { pct: cov ? Math.round(cov.ratio * 100) : -1, ms: S.t.cov, n: times.length, objs: regs.length,
                    wc: pm && pm.webcodecs || 0, seek: pm && (pm.seek || 0) + (pm.mixed || 0) || 0 });
            } catch (e) {
                if (ac.signal.aborted || isAbort(e)) { feat('ro_probe_cancel', { at_ms: Math.round(performance.now() - tCov) }); return; }
                if (my === estimateSeq) renderCoverage('unknown');
                ae('tool_error', { error_type: 'ro_coverage', msg: (e && e.message) || String(e) });
            } finally {
                if (sessQ.probeAbort === ac) sessQ.probeAbort = null;
            }
        });
    }

    /* ⛔ An estimate made BEFORE the click was measured and rejected
       (17.09.2026). The coverage probe reconstructs frames of this clip on
       this device, so pricing the export from it looked sound — but the probe
       reads a ±12-frame window for EVERY sample and throws it away, while the
       export slides one window along, and the cost per frame depends on the
       hole the object leaves. Calibration on three real clips gave
       export_ms / (probe_ms/probe_n × frames) = 0.078 / 0.365 / 0.122 — a 4.7×
       spread, i.e. "one minute" and "five" from the same number. What the page
       says instead is measured on the export itself, from its first frames
       (see setProgress / firstEtaAt below). */

    function renderCoverage(kind, cov, frac) {
        var box = $('roCov'), v = $('roCovV'), meter = $('roMeter'), bar = $('roMeterReal'), txt = $('roCovText');
        if (!box) return;
        box.hidden = !readyObjects().length || S.state === 'rendering' || S.state === 'done' || tightMemory();
        if (kind === 'pending') {
            meter.classList.add('is-pending'); bar.style.width = '0%';
            v.textContent = ''; txt.textContent = T.cov_checking || '';
            return;
        }
        if (kind === 'progress') {
            if (cov && frac >= 0.28) {
                var p = Math.round(cov.ratio * 100);
                meter.classList.remove('is-pending'); bar.style.width = p + '%';
                v.textContent = tr('cov_partial', { pct: p }, '≈' + p + '%');
            }
            txt.textContent = T.cov_checking || '';
            return;
        }
        if (kind === 'unknown' || !cov) {
            meter.classList.add('is-pending'); bar.style.width = '0%';
            v.textContent = ''; txt.textContent = T.cov_unknown || '';
            return;
        }
        var pct = Math.round(cov.ratio * 100);
        meter.classList.remove('is-pending');
        bar.style.width = pct + '%';
        v.textContent = tr('cov_value', { pct: pct }, pct + '%');
        var key = pct >= 90 ? 'cov_good' : (pct >= 55 ? 'cov_mid' : (pct >= 10 ? 'cov_low' : 'cov_none'));
        txt.textContent = tr(key, { pct: pct, rest: 100 - pct });
        try { window.__roLastEstimate = pct; } catch (_) { }
    }

    /* ── the «Фон» stage: model, preview ─────────────────────────────────── */
    function bgBackdrop() {
        return window.BgReplace ? window.BgReplace.backdrop(S.bg.id, { color: S.bg.custom, strength: S.bg.strength }) : null;
    }
    function bgStage() {
        if (!S.bgStage && window.BgReplace) S.bgStage = window.BgReplace.createStage({ engine: 'ai' });
        return S.bgStage;
    }
    /* The model (≈14 MB, then cached) is fetched on the first use of the
       switch. The download is NOT tied to one caller's signal: it serves the
       preview and the export, and a preview superseded by a new tap must not
       throw away what the export is about to need — the signal only stops that
       caller's wait. */
    var bgLoad = null;
    function bgEnsure(signal, onFrac) {
        var stg = bgStage();
        if (!stg) return Promise.resolve(false);
        if (S.bgModel === 'ready') return Promise.resolve(true);
        var R = window.VbrRvmEngine;
        if (R && R.onModelProgress) R.onModelProgress(function (got, total) { if (total && onFrac) { try { onFrac(Math.min(1, got / total)); } catch (_) { } } });
        if (!bgLoad) {
            var t0 = performance.now();
            S.bgModel = 'loading'; S.bgErr = null;
            bgLoad = stg.ready().then(function () {
                S.bgModel = 'ready';
                feat('ro_bg_model', { ok: 1, ms: Math.round(performance.now() - t0), ep: stg.provider() || '' });
                return true;
            }, function (e) {
                S.bgModel = 'failed'; S.bgErr = 'model'; bgLoad = null;
                feat('ro_bg_model', { ok: 0, ms: Math.round(performance.now() - t0), msg: String(e && e.message || e).slice(0, 120) });
                renderBgStep();
                return false;
            });
        }
        if (!signal) return bgLoad;
        return new Promise(function (resolve) {
            if (signal.aborted) { resolve(false); return; }
            var onAbort = function () { resolve(false); };
            signal.addEventListener('abort', onAbort, { once: true });
            bgLoad.then(function (ok) { signal.removeEventListener('abort', onAbort); resolve(ok); });
        });
    }
    function copyCanvas(src) {
        var c = document.createElement('canvas');
        c.width = src.width; c.height = src.height;
        c.getContext('2d').drawImage(src, 0, 0);
        return c;
    }
    function drawBgInto(cx, cv, matte, base) {
        var out = window.BgReplace.compose(matte, bgBackdrop(), base, null);
        cx.clearRect(0, 0, cv.width, cv.height);
        cx.drawImage(out, 0, 0, cv.width, cv.height);
    }
    /* The preview's background: the frame on `cv` (already cleaned of the
       marked objects) goes behind the people it holds. The matte and that frame
       are kept, so a swatch change recomposes in milliseconds instead of asking
       the net again. */
    async function bgPreviewOnto(cv, cx, t, signal, my) {
        var ok = await bgEnsure(signal, function (f) { if (my === S.compareSeq) setBusy(T.bg_model_loading || '', f, 'preview'); });
        if (!ok || signal.aborted || my !== S.compareSeq) return null;
        setBusy(T.bg_preview_busy || '', null, 'preview');
        var base = copyCanvas(cv);
        var t0 = performance.now();
        var matte = await bgStage().still(base, t);
        if (signal.aborted || my !== S.compareSeq) return null;
        drawBgInto(cx, cv, matte, base);
        var share = window.BgReplace.shareOf(matte);
        S.bgPrev = { t: t, base: base, matte: matte };
        S.bgShare = share;
        var pct = share == null ? -1 : Math.round(share * 1000) / 10;
        feat('ro_bg_preview', { ms: Math.round(performance.now() - t0), people: pct, bg: S.bg.id, objs: readyObjects().length, ep: bgStage().provider() || '' });
        if (share != null && share < BG_NO_PEOPLE) feat('ro_bg_no_people', { people: pct });
        try { window.__roLastBgPreview = { people: share, bg: S.bg.id }; } catch (_) { }   // stands read this
        return { share: share };
    }
    function recomposeBg() {
        if (!S.compare || !S.compare.bg || !S.bgPrev || !window.BgReplace) return false;
        drawBgInto(actx, after, S.bgPrev.matte, S.bgPrev.base);
        return true;
    }

    /* ── compare: one frame of the real result ───────────────────────────── */
    async function showCompare(t, auto) {
        var objs = readyObjects().length, useBg = bgOn() && !!window.BgReplace;
        if ((!objs && !useBg) || S.exporting) return;
        var my = ++S.compareSeq;
        pause();
        var pv = performance.now();
        if (useBg) { var ap0 = $('roApp'); if (ap0) ap0.setAttribute('data-bg-preview', 'pending'); }
        setBusy(objs ? (T.preview_busy || '') : (T.bg_preview_busy || ''), null, 'preview');
        var ok = false;
        await withSession('preview', async function () {
            if (my !== S.compareSeq || S.exporting) return;
            var ac = new AbortController();
            sessQ.previewAbort = ac;
            try {
                var c = Math.round(t * S.fps), tc = c / S.fps;
                await seekTo(tc);
                fitCanvas();
                actx.clearRect(0, 0, after.width, after.height);
                actx.drawImage(S.video, 0, 0, after.width, after.height);
                if (objs) {
                    var sess = ensureSession();
                    sess.setRegions(readyRegions());
                    await sess.previewAt(tc, actx, after.width, after.height, { dense: true, signal: ac.signal });
                    if (my !== S.compareSeq || ac.signal.aborted) return;
                }
                if (useBg) {
                    var bgr = await bgPreviewOnto(after, actx, tc, ac.signal, my);
                    if (!bgr) {
                        // the model did not arrive: the objects' own frame still
                        // stands (the note in step 2 says why the background is not on it)
                        if (objs && S.bgModel === 'failed' && my === S.compareSeq && !ac.signal.aborted) { S.compare = { t: tc, bg: false }; ok = true; }
                        return;
                    }
                }
                S.compare = { t: tc, bg: useBg };
                ok = true;
            } catch (e) {
                if (!isAbort(e) && !ac.signal.aborted) {
                    ae('tool_error', { error_type: 'ro_preview', msg: (e && e.message) || String(e) });
                    toast(T.err_export || '', 'error');
                }
            } finally {
                if (sessQ.previewAbort === ac) sessQ.previewAbort = null;
            }
        });
        clearBusy('preview');
        if (ok && my === S.compareSeq) {
            $('roAfter').hidden = false;
            $('roWipe').hidden = false;
            setWipe(S.wipe == null ? 0.5 : S.wipe);
            drawScene();
            var btn = $('roCompare'); if (btn) { $('roCompareText').textContent = T.btn_compare_off || ''; btn.setAttribute('aria-pressed', 'true'); }
            // A fully gray frame is not a finished background — it is a frame
            // the net found nobody in. Say it where the eyes are.
            if (S.compare.bg && S.bgShare != null && S.bgShare < BG_NO_PEOPLE) setPill(T.pill_bg_no_people || '', 'warn', true);
            // seen, but not for sale at this length: say so where the eyes are
            else if (S.compare.bg && bgOverCap()) setPill(tr('pill_bg_cap', { cap: bgCapSec() }), 'warn', true);
            else setPill(T.pill_preview || '', 'ok', true);
            feat('ro_preview', { ms: Math.round(performance.now() - pv), auto: auto ? 1 : 0, objs: readyObjects().length, bg: S.compare.bg ? 1 : 0 });
        }
        var app = $('roApp');
        if (app && bgOn() && my === S.compareSeq) app.setAttribute('data-bg-preview', ok && S.compare && S.compare.bg ? 'ready' : 'none');   // stands wait on this
        renderBgStep();
        if (!S.coverageFinal) scheduleEstimate(60);
    }
    function hideCompare() {
        S.compareSeq++;
        // Whatever it was computing is no longer wanted; let the decoder go.
        if (sessQ.previewAbort) { try { sessQ.previewAbort.abort(); } catch (_) { } }
        if (!S.compare && $('roWipe').hidden) return;
        S.compare = null;
        $('roAfter').hidden = true;
        $('roWipe').hidden = true;
        var btn = $('roCompare'); if (btn) { $('roCompareText').textContent = T.btn_compare || ''; btn.setAttribute('aria-pressed', 'false'); }
        drawScene();
    }
    function setWipe(f) {
        S.wipe = Math.max(0, Math.min(1, f));
        var box = $('roBox');
        if (box) box.style.setProperty('--ro-wipe', (S.wipe * 100).toFixed(2) + '%');
        var k = $('roWipeKnob'); if (k) k.setAttribute('aria-valuenow', String(Math.round(S.wipe * 100)));
    }

    /* ── export ──────────────────────────────────────────────────────────── */
    async function probeEncoders() {
        ENC.probed = false;
        if (!window.CanvasVideoExport || !S.vw) return;
        var dims = { width: even(S.vw), height: even(S.vh), fps: Math.max(1, Math.round(S.fps || 30)) };
        async function ask(fmt) {
            try {
                return await window.CanvasVideoExport.isSupported(Object.assign({ format: fmt, audioSampleRate: 48000, audioChannels: 2 }, dims));
            } catch (e) { return { ok: false, reason: 'probe-threw' }; }
        }
        var mp4 = await ask('mp4'), webm = await ask('webm');
        var videoOk = (typeof VideoEncoder !== 'undefined');
        ENC.mp4 = mp4.ok || (videoOk && (mp4.reason === 'no-audio-encoder' || mp4.reason === 'no-webcodecs'));
        ENC.webm = webm.ok || (videoOk && (webm.reason === 'no-audio-encoder' || webm.reason === 'no-webcodecs'));
        /* Per CONTAINER, not per browser: Firefox encodes Opus and not AAC, and
           one shared boolean ended an MP4 export in NotSupportedError. */
        ENC.audioMp4 = !!mp4.ok;
        ENC.audioWebm = !!webm.ok;
        ENC.audio = mp4.ok || webm.ok;
        ENC.probed = true;
        feat('ro_encoders', { mp4: ENC.mp4 ? 1 : 0, webm: ENC.webm ? 1 : 0, audio: ENC.audio ? 1 : 0,
            amp4: ENC.audioMp4 ? 1 : 0, awebm: ENC.audioWebm ? 1 : 0, r_mp4: mp4.reason || 'ok', r_webm: webm.reason || 'ok' });
    }

    /* Which container this browser can write the VIDEO of. Sound no longer
       takes part: it is the source's own track, attached after the render. */
    function resolveExport(requested) {
        if (!ENC.probed) return { fmt: requested, switched: false };
        var fmt = requested, switched = false;
        if (fmt === 'mp4' && !ENC.mp4 && ENC.webm) { fmt = 'webm'; switched = true; }
        else if (fmt === 'webm' && !ENC.webm && ENC.mp4) { fmt = 'mp4'; switched = true; }
        return { fmt: fmt, switched: switched };
    }

    /* The sound is the ORIGINAL track, carried over — never decoded and
       re-encoded inside the render.
       Until 2026-09-15 the render took `await file.arrayBuffer()` (the WHOLE
       clip), copied it once more for decodeAudioData, decoded it to PCM and
       encoded AAC again: 100–700 MB at once for a 50–350 MB iPhone MOV, in the
       same tab that already held the segmenter. On iOS that is the jetsam line —
       prod 09.09–14.09: of 22 iPhone renders 3 delivered, and the events right
       before the tab reloaded were that very decode failing over. It was also the
       raw decodeAudioData on mp4 the house bans (HE-AAC doubled on Yandex).
       Now: the render is silent, and the source's audio track is attached
       after it —
         1. mp4 out, mp4-family in: MP4TrackRemux.replaceAudio splices the
            source's `soun` trak into the render's moov; sample bytes stay
            file.slice() references (no copy, no codec, milliseconds);
         2. anything it declines (an edit list, a WebM target, a container it
            does not read): FFmpeg `-c:a copy` when the codec fits the target,
            a re-encode only when it does not;
         3. if even that fails the silent video is kept and the loss is SAID. */
    var AUDIO_COPY_OK = { mp4: /^(aac|mp4a|mp3|alac)/i, webm: /^(opus|vorbis)/i };
    async function attachSourceAudio(videoBlob, fmt, signal) {
        var src = S.origFile || S.file;
        if (!src) { toast(T.audio_dropped || '', 'warning'); return videoBlob; }
        var ext = fmt === 'webm' ? 'webm' : 'mp4';
        var mime = fmt === 'webm' ? 'video/webm' : 'video/mp4';
        var t0 = performance.now();
        var vFile = new File([videoBlob], 'render.' + ext, { type: mime });
        var declined = '';
        if (fmt === 'mp4' && window.MP4TrackRemux && window.MP4TrackRemux.looksSupported(src)) {
            try {
                var rr = await window.MP4TrackRemux.replaceAudio(vFile, src, { mime: mime });
                if (signal && signal.aborted) throw abortedError();
                if (rr && rr.blob && rr.blob.size > videoBlob.size) {
                    feat('ro_audio', { via: 'remux', ms: Math.round(performance.now() - t0), parts: rr.parts || 0 });
                    return rr.blob;
                }
                declined = 'remux-empty';
            } catch (e) {
                if (isAbort(e)) throw e;
                declined = (e && e.remuxReason) || String(e && e.message || e).slice(0, 40);
            }
        } else declined = fmt === 'mp4' ? 'not-mp4-family' : 'webm-target';
        var codecs = S.srcAudioCodecs || [];
        var copyable = codecs.length === 1 && AUDIO_COPY_OK[ext].test(codecs[0]);
        var attempts = copyable ? ['copy', 'encode'] : ['encode'];
        var lastErr = null;
        for (var i = 0; i < attempts.length; i++) {
            try {
                if (!window.RoSource) throw new Error('RoSource missing');
                await window.RoSource.ensureFfmpeg({ ffmpegUrl: CFG.ffmpegUrl });
                if (signal && signal.aborted) throw abortedError();
                var aCodec = attempts[i] === 'copy' ? ['-c:a', 'copy']
                    : (fmt === 'webm' ? ['-c:a', 'libopus', '-b:a', '160k'] : ['-c:a', 'aac', '-b:a', '192k']);
                var args = ['-map', '0:v:0', '-map', '1:a:0?', '-c:v', 'copy'].concat(aCodec, ['-shortest']);
                var res = window.FFmpegProcessor.processMultiStreaming
                    ? await window.FFmpegProcessor.processMultiStreaming([vFile, src], args, 'muxed.' + ext)
                    : await window.FFmpegProcessor.processMulti([vFile, src], args, 'muxed.' + ext);
                if (signal && signal.aborted) throw abortedError();
                if (res && res.blob && res.blob.size > videoBlob.size * 0.5) {
                    feat('ro_audio', { via: 'ffmpeg-' + attempts[i], declined: declined, ms: Math.round(performance.now() - t0) });
                    return res.blob;
                }
                throw new Error('mux produced ' + (res && res.blob ? res.blob.size : 0) + ' bytes');
            } catch (e) {
                if (isAbort(e)) throw e;
                lastErr = e;
            }
        }
        feat('ro_audio_mux_failed', { declined: declined, msg: String(lastErr && lastErr.message || lastErr).slice(0, 120) });
        toast(T.audio_dropped || '', 'warning');
        return videoBlob;
    }

    /* Memory back before a render, where memory is the limit. The frames and
       the segmenter live in the selection worker, which the render never reads;
       ending the worker is the only way wasm memory is ever returned (the
       ratchet in CLAUDE.md). The reconstruction session is rebuilt too, so the
       probe's and the preview's windows do not ride along. A later tap reads the
       clip again (restoreAnalysis). */
    function releaseForRender() {
        var freed = [];
        if (S.worker && !S.wpending.size) {
            try { S.worker.terminate(); } catch (_) { }
            S.worker = null;
            S.seg = { state: 'idle', provider: null, promise: null, retried: false };
            S.framesReady = false;
            S.analysisReleased = true;
            freed.push('select');
        }
        if (S.session) { try { S.session.dispose(); } catch (_) { } S.session = null; freed.push('recon'); }
        if (freed.length) feat('ro_mem_released', { what: freed.join('+') });
    }
    var restoring = null;
    function restoreAnalysis() {
        if (restoring) return restoring;
        restoring = (async function () {
            setState('reading');
            setPill(T.pill_reading || '', 'busy');
            await ensureWorker();
            if (!tightMemory()) segInit();
            await readSamples();
            S.analysisReleased = false;
            clearBusy();
            if (tightMemory()) segInit();
            setState(hasWork() ? 'ready' : 'select');
            renderRail();
            drawScene();
            if (S.pendingTap) {
                var p = S.pendingTap; S.pendingTap = null;
                if (p.mode) setMode(p.mode);
                await tapAt(p.x, p.y);
            } else if (S.pendingBox) {
                var bq = S.pendingBox; S.pendingBox = null;
                await boxDrawn(bq.x, bq.y, bq.w, bq.h);
            } else pillForState();
        })().catch(function (e) {
            setWorking(false);
            clearBusy();
            if (!isAbort(e)) {
                toast(T.err_open || '', 'error');
                ae('tool_error', { error_type: 'ro_restore', msg: String(e && e.message || e).slice(0, 160) });
            }
        }).finally(function () { restoring = null; });
        return restoring;
    }

    /* The button names the whole job: what goes, and whether the background
       goes with it. */
    function exportLabel() {
        var n = readyObjects().length;
        if (bgOn()) {
            if (!n) return T.btn_export_bg || '';
            return n > 1 ? tr('btn_export_both_n', { n: n }) : (T.btn_export_both || '');
        }
        return n > 1 ? tr('btn_export_n', { n: n }) : (T.btn_export || '');
    }
    function readyPill() {
        if (bgOn() && !readyObjects().length) return tr('pill_bg_ready', { action: exportLabel() });
        return tr('pill_ready', { action: exportLabel() });
    }
    /* What the render is doing, for the pill, the stage label and the name. */
    function renderKind() {
        var n = readyObjects().length, bg = bgWorks();
        return bg ? (n ? 'both' : 'bg') : 'obj';
    }
    function renderingPill(p) {
        var k = S.renderKind === 'bg' ? 'pill_rendering_bg' : (S.renderKind === 'both' ? 'pill_rendering_both' : 'pill_rendering');
        return tr(k, { pct: p });
    }
    function workLabel() {
        if (S.renderKind === 'bg') return T.stage_bg || T.stage_rebuild || '';
        if (S.renderKind === 'both') return T.stage_both || T.stage_rebuild || '';
        return T.stage_rebuild || '';
    }

    var baseTitle = document.title;
    async function runExport() {
        if (S.exporting) return;
        var regs = readyRegions();
        var useBg = bgOn();
        // Refused BEFORE the work: the button is disabled for this, so reaching
        // here means a keyboard shortcut or a stale click — say why, do nothing.
        if (useBg && bgOverCap()) {
            feat('ro_bg_capped', { dur: Math.round(S.dur), cap: bgCapSec(), via: 'export' });
            renderBgStep(); pillForState();
            return;
        }
        if (!regs.length && !useBg) { setPill(T.err_no_object || '', 'warn', true); return; }
        // __toolLimit.begin() is SYNCHRONOUS and hands back a Token (or null at
        // the daily wall — the note is shown by the module itself). Settle with
        // token.commit() / token.abort().
        var lim = window.__toolLimit;
        var ticket = null;
        if (lim && lim.begin) {
            // Keyed on the file the user PICKED, not on the ladder's proxy: a
            // repeat of the same clip on the same day is meant to be free.
            var capFile = S.origFile || S.file;
            var fkey = (lim.fileKey && capFile) ? lim.fileKey(capFile) : null;
            ticket = lim.begin(CFG.slug, fkey);
            if (!ticket) return;
        }
        var probeWasRunning = !S.coverageFinal;
        var ac = new AbortController();
        S.exportAbort = ac;
        S.exporting = true;
        estimateSeq++;
        hideCompare();
        pause();
        if (S.boxTool) setBoxTool(false, 'export');
        var want = ($('roFormat') || {}).value || 'mp4';
        var qual = ($('roQuality') || {}).value || 'high';
        var crf = qual === 'high' ? 18 : qual === 'medium' ? 23 : 28;
        var t0 = performance.now();
        S.etaFirstAt = null; S.etaFirst = null; S.renderT0 = null;   // one export, one first-estimate mark
        S.renderKind = renderKind();
        setState('rendering');
        setPill(renderingPill(0), 'busy', true);
        stage('processing');
        if (window.__crashMarker) { try { window.__crashMarker.arm(CFG.slug, 'export', { mb: (S.origFile ? S.origFile.size : 0) / 1048576, video: 1 }); } catch (e) { } }
        setProgress(0, workLabel());

        try {
            await withSession('export', async function () {
                var probeWait = 0;
                if (!ENC.probed) {
                    var tp = performance.now();
                    setPill(T.pill_encoders || renderingPill(0), 'busy', true);
                    setProgress(null, T.stage_encoders || workLabel());
                    if (!S.encProbe) S.encProbe = probeEncoders().catch(function () { });
                    await Promise.race([S.encProbe, new Promise(function (res) { setTimeout(res, ENC_PROBE_WAIT_MS); })]);
                    probeWait = Math.round(performance.now() - tp);
                    if (!ENC.probed) feat('ro_encoders_unprobed', { waited_ms: probeWait });
                }
                var plan = resolveExport(want);
                var fmt = plan.fmt;
                if (plan.switched) {
                    toast(tr('fmt_switched', { fmt: fmt.toUpperCase() }), 'warning');
                    feat('ro_format_fallback', { from: want, to: fmt });
                }
                var wantAudio = S.srcHasAudio !== false;
                if (window.__aiOutput && window.__aiOutput.reset) { try { window.__aiOutput.reset(); } catch (_) { } }
                var tight = tightMemory();
                if (tight) releaseForRender();
                /* The matting model before the first frame: its download has a
                   bar of its own, and a render that stalls on it would read as
                   a frozen export. Usually it is already warm from the preview. */
                var bgBd = null;
                if (useBg) {
                    setProgress(null, T.bg_model_loading || workLabel());
                    var bgOk = await bgEnsure(ac.signal, function (f) { setProgress(null, (T.bg_model_loading || '') + ' ' + Math.round(f * 100) + '%'); });
                    if (ac.signal.aborted) throw abortedError();
                    if (!bgOk) throw Object.assign(new Error('bg-model-failed'), { bgModel: true });
                    bgStage().begin();
                    bgBd = bgBackdrop();
                    setProgress(null, workLabel());
                }
                feat('ro_export_start', {
                    fmt: fmt, crf: crf, dur: Math.round(S.dur), kfs: regs.reduce(function (s, r) { return s + r.keyframes.length; }, 0),
                    objs: regs.length, promised_cov: S.coverage ? Math.round(S.coverage.ratio * 100) : -1,
                    before_estimate: probeWasRunning ? 1 : 0,
                    audio: wantAudio ? 1 : 0, tight: tight ? 1 : 0, probe_wait_ms: probeWait,
                    track_ms: S.t.track || 0, cov_ms: S.t.cov || 0,
                    eta_first_ms: S.etaFirstAt == null ? -1 : S.etaFirstAt, eta_first_s: S.etaFirst == null ? -1 : S.etaFirst,
                    bg: useBg ? S.bg.id : '', bg_ep: useBg ? (bgStage().provider() || '') : ''
                });

                var dims = outDims();
                S.lastOut = dims;
                var sess = ensureSession();
                sess.setRegions(regs);
                var lastLive = 0;
                var blob = await sess.export({
                    format: fmt, qualityCrf: crf,
                    outWidth: dims.w, outHeight: dims.h,
                    // Always silent: the source's own track is attached after
                    // the render (attachSourceAudio) — never decoded here.
                    mute: true,
                    signal: ac.signal,
                    onStallAbort: function () { try { ac.abort(); } catch (_) { } },
                    onStage: function (s) { setProgress(null, s === 'encode' ? (T.stage_encode || '') : workLabel()); },
                    // The background, frame by frame and in order (the matting
                    // net is recurrent), on the frame the objects were already
                    // taken out of.
                    postFrame: useBg ? function (out, c, tt) { return bgStage().frame(out, tt, bgBd, null); } : null,
                    perFrameCost: useBg ? BG_FRAME_COST : 0,
                    // The render itself, on the stage, while it happens: the
                    // object disappearing frame by frame is the best progress
                    // bar this tool can have.
                    onFramePreview: function (out, c, N, tt) {
                        var now = performance.now();
                        if (now - lastLive < 220) return;
                        lastLive = now;
                        try { sctx.drawImage(out, 0, 0, scene.width, scene.height); } catch (_) { }
                        if (uctx) uctx.clearRect(0, 0, ui.width, ui.height);
                        if (timeline) timeline.setTime(tt != null ? tt : c / S.fps);
                    },
                    onProgress: function (f) {
                        f = Math.max(0, Math.min(1, f));
                        var el = (performance.now() - t0) / 1000;
                        /* When the wait gets a number. It used to be 12 % of the
                           render — on the three-hour export in the 03–17.09 data
                           that is twenty-one minutes of a bar with no number, and
                           on the 6-minute median, forty seconds. The render is
                           frame-paced, so a few per cent of it already prices a
                           frame; below that the estimate swings, so it waits for
                           BOTH a little progress and a little wall time. */
                        /* The first 12 % of `f` is the read/analyse phase, which
                           runs at a completely different rate from the render —
                           extrapolating the whole export from it put the first
                           estimate 3.8× short (dogwalk, measured). So the clock
                           for the estimate starts when the RENDER starts, and
                           the estimate covers the render only. */
                        if (f > 0.12 && S.renderT0 == null) S.renderT0 = performance.now();
                        var eta = null;
                        if (S.renderT0 != null) {
                            var rEl = (performance.now() - S.renderT0) / 1000;
                            var rf = (f - 0.12) / 0.88;
                            if (rf >= 0.05 && rEl >= 2) eta = rEl / rf * (1 - rf);
                        }
                        if (eta != null && S.etaFirstAt == null) {
                            S.etaFirstAt = Math.round(el * 1000);
                            S.etaFirst = Math.round(el + eta);
                        }
                        setProgress(f, null, eta);
                    }
                });
                if (!blob || !blob.size) throw new Error('empty result');
                if (wantAudio) {
                    setProgress(null, T.stage_audio || '');
                    blob = await attachSourceAudio(blob, fmt, ac.signal);
                }

                S.resultBlob = blob;
                var srcName = (S.origFile && S.origFile.name) || (S.file && S.file.name) || 'video';
                // the name says what was done, so a second export of the same clip
                // with the other job does not look like the first one
                var suffix = useBg ? (regs.length ? '_no_object_new_bg' : '_new_bg') : '_no_object';
                S.resultName = srcName.replace(/\.[^.]+$/, '') + suffix + '.' + (fmt === 'webm' ? 'webm' : 'mp4');
                var bgStats = useBg ? bgStage().stats() : null;
                try { window.__roLastBg = bgStats; } catch (_) { }   // stands read this

                // Deliver, do not offer.
                var cov = sess.coverage();
                window.__roLastCoverage = cov ? Math.round(cov.ratio * 100) : null;
                var synth = sess.synthStats ? await sess.synthStats() : null;
                window.__roLastSynth = synth || null;
                if (synth) feat('ro_synth', { fresh: synth.fresh, borrowed: synth.borrowed, gan: synth.gan, gan_frames: synth.ganFrames, telea: synth.telea, ai: window.RO_AI ? 1 : 0, reach: synth.reach || 0, reach_hits: synth.reachHits || 0, reach_mis: synth.reachMisaligned || 0 });
                // `conditional` in config/ai-output.php: marked as AI output only
                // when the GAN actually drew pixels into this file. Decided BEFORE
                // the delivery: the download primitive stamps the mark into the file
                // it saves (timbrica-core __downloadFile), so a used() after it never
                // reached a single file (found 02.10.2026 next to /remove-text-from-video).
                if (synth && synth.gan > 0 && window.__aiOutput) { try { window.__aiOutput.used(); } catch (_) { } }

                if (window.__downloadFile) window.__downloadFile(blob, S.resultName);
                feat('ro_download', { mb: Math.round(blob.size / 1048576), auto: 1 });
                var ms = Math.round(performance.now() - t0);
                if (window.__aeToolComplete) window.__aeToolComplete({
                    ms: ms, sizeMB: Math.round(blob.size / 1048576), fmt: fmt,
                    coverage: cov ? Math.round(cov.ratio * 100) : -1,
                    promised_cov: S.coverage ? Math.round(S.coverage.ratio * 100) : -1,
                    audio: wantAudio ? 1 : 0, tight: tight ? 1 : 0, objs: regs.length,
                    kfs: regs.reduce(function (s, r) { return s + r.keyframes.length; }, 0),
                    track_ms: S.t.track || 0, cov_ms: S.t.cov || 0,
                    eta_first_ms: S.etaFirstAt == null ? -1 : S.etaFirstAt, eta_first_s: S.etaFirst == null ? -1 : S.etaFirst,
                    bg: useBg ? S.bg.id : '',
                    bg_ms: bgStats ? bgStats.msPerFrame : -1,
                    bg_people: bgStats && bgStats.share != null ? Math.round(bgStats.share * 100) : -1
                });
                if (ticket) ticket.commit();
                showResult(blob, cov);
                // No toast of our own: the download primitive confirms the save
                // itself, and the stage and the rail already say it — a fourth
                // copy of the same sentence was the done screen's loudest part.
                if (window.__announce) window.__announce(T.export_done || 'Done');
            });
        } catch (e) {
            // Nothing in here may throw: an exception on the failure path left
            // the progress bar frozen and the real error invisible once.
            try { if (ticket) ticket.abort(); } catch (e2) { }
            var cancelled = ac.signal.aborted || isAbort(e);
            try {
                if (cancelled) feat('ro_export_cancel', { at_ms: Math.round(performance.now() - t0), bg: useBg ? 1 : 0 });
                else if (e && e.bgModel) {
                    // the matting model did not load: say THAT, not «try another
                    // format» — the note in the step stays until a retry
                    toast(T.bg_err_model || T.err_export || '', 'error');
                    stage('error', { reason: 'bg_model' });
                } else {
                    toast(T.err_export || 'Export failed. Try a different format or a shorter clip.', 'error');
                    ae('tool_error', { error_type: 'ro_export', msg: (e && e.message) || String(e), bg: useBg ? 1 : 0 });
                    stage('error', { reason: 'export' });
                }
            } catch (e3) { }
            if (window.console && !cancelled) console.error('[remove-object] export failed:', e);
            S.exporting = false;
            S.exportAbort = null;
            setState(hasWork() ? 'ready' : 'select');
            drawScene();
            pillForState();
            scheduleEstimate(400);
        } finally {
            S.exporting = false;
            S.exportAbort = null;
            document.title = baseTitle;
            if (window.__crashMarker) { try { window.__crashMarker.disarm(CFG.slug); } catch (e4) { } }
        }
    }

    function even(n) { n = Math.round(n); return n % 2 ? n - 1 : n; }

    function showResult(blob, cov) {
        var v = $('roResultVideo');
        if (S.resultUrl) { try { URL.revokeObjectURL(S.resultUrl); } catch (_) { } }
        S.resultUrl = null;
        if (v) {
            try { S.resultUrl = URL.createObjectURL(blob); v.src = S.resultUrl; v.currentTime = 0; } catch (e) { }
            v.hidden = false;
        }
        var note = $('roDoneNote');
        if (note) note.textContent = (T.result_saved || '') + (cov ? ' ' + tr('done_cov', { pct: Math.round(cov.ratio * 100) }) : '');
        var rp = $('roResPerk');
        if (rp) {
            var d = S.lastOut;
            var sellRes = !!(d && d.scaled && !planIsPro() && planSoldHere());
            rp.hidden = !sellRes;
            if (sellRes) {
                $('roResPerkText').textContent = tr('done_res_upsell', { res: d.short + 'p', src: srcShort() + 'p', plan: planName() });
                proLink($('roResPerkCta'), 'resolution');
                perkShown('resolution');
            }
        }
        setDoneCompare(false);
        setState('done');
        setPill(T.pill_done || '', 'ok', true);
        if (timeline) timeline.setTime(0);
        updateTime();
    }

    /* The wipe over the finished video: the source frame is drawn on the scene
       canvas underneath at the result's time, and the result element is
       clipped to the right of the knob. */
    function setDoneCompare(on) {
        var rv = $('roResultVideo');
        S.doneCompare = !!on;
        if (rv) rv.classList.toggle('is-wipe', S.doneCompare);
        $('roWipe').hidden = !S.doneCompare;
        $('roCompareText').textContent = S.doneCompare ? (T.btn_compare_off || '') : (T.btn_compare || '');
        $('roCompare').setAttribute('aria-pressed', S.doneCompare ? 'true' : 'false');
        if (S.doneCompare) { setWipe(S.wipe == null ? 0.5 : S.wipe); syncOriginal(true); }
        else if (S.video && !S.video.paused) { try { S.video.pause(); } catch (_) { } }
    }
    function syncOriginal(force) {
        if (!S.doneCompare || !S.video) return;
        var rv = $('roResultVideo');
        if (!rv) return;
        if (force || Math.abs(S.video.currentTime - rv.currentTime) > 0.06) {
            seekTo(rv.currentTime).then(function () { if (sctx) { try { sctx.drawImage(S.video, 0, 0, scene.width, scene.height); } catch (_) { } } });
        }
    }

    function leaveResult() {
        var v = $('roResultVideo');
        setDoneCompare(false);
        if (v) { try { v.pause(); } catch (_) { } v.hidden = true; }
        setState(hasWork() ? 'ready' : 'select');
        drawScene();
        pillForState();
        if (!S.coverageFinal) scheduleEstimate(200);
    }

    /* ── playback ────────────────────────────────────────────────────────── */
    function playEl() { return S.state === 'done' ? $('roResultVideo') : S.video; }
    function play() {
        var v = playEl();
        if (!v || !S.dur || S.exporting) return;
        if (S.state !== 'done') hideCompare();
        if (v === S.video && v.currentTime >= S.dur - 0.05) { try { v.currentTime = 0; } catch (_) { } }
        var p = v.play();
        if (p && p.catch) p.catch(function () { });
        if (S.state === 'done' && S.doneCompare && S.video) {
            try { S.video.currentTime = v.currentTime; var p2 = S.video.play(); if (p2 && p2.catch) p2.catch(function () { }); } catch (_) { }
        }
        S.playing = true;
        setPlayIcon(true);
        loop();
        feat('ro_play', { st: S.state });
    }
    function pause() {
        [S.video, $('roResultVideo')].forEach(function (v) { if (v && !v.paused) { try { v.pause(); } catch (_) { } } });
        if (S.playing) { S.playing = false; setPlayIcon(false); drawScene(); }
    }
    /* requestAnimationFrame, not requestVideoFrameCallback: the display video
       lives in a 1×1 transparent host (iOS parks media it cannot see), and a
       frame callback is only promised for frames that get composited. */
    var lastDrawnT = -1;
    function loop() {
        var v = playEl();
        if (!S.playing || !v) return;
        if (v.paused || v.ended) { S.playing = false; setPlayIcon(false); drawScene(); return; }
        if (v === S.video) { if (v.currentTime !== lastDrawnT) { lastDrawnT = v.currentTime; drawScene(); } }
        else {
            updateTime();
            if (S.doneCompare && S.video) {
                // keep the source under the wipe in step with the result
                if (Math.abs(S.video.currentTime - v.currentTime) > 0.12) { try { S.video.currentTime = v.currentTime; } catch (_) { } }
                try { sctx.drawImage(S.video, 0, 0, scene.width, scene.height); } catch (_) { }
            }
        }
        requestAnimationFrame(loop);
    }
    function setPlayIcon(on) {
        var b = $('roPlay'), ico = $('roPlayIco');
        if (!b || !ico) return;
        ico.innerHTML = on ? '<path d="M7 5h4v14H7zM13 5h4v14h-4z"/>' : '<path d="M8 5v14l11-7z"/>';
        b.setAttribute('aria-label', on ? (T.pause || '') : (T.play || ''));
    }
    function updateTime() {
        var v = playEl();
        var t = v ? v.currentTime : 0;
        var el = $('roTime');
        if (el) textWrite(el, fmtTime(t) + ' / ' + fmtTime(S.dur));
        if (timeline) timeline.setTime(t);
    }

    /* ── drawing ─────────────────────────────────────────────────────────── */
    var _ovCanvas = null;
    function silhouetteOf(o, t) {
        if (!window.RtMaskBuilder || !S.pw || !o) return null;
        var r = o.region;
        if (r && r.masks) {
            if (t < (r.startTime || 0) - 1e-3 || t > (r.endTime == null ? S.dur : r.endTime) + 1e-3) return null;
            var b = boxAt(r, t);
            if (!b) return null;
            var ras = window.RtMaskBuilder.rasterize([{ id: r.id, shape: r.shape, x: b.x, y: b.y, w: b.w, h: b.h, masks: r.masks, keyframes: r.keyframes, startTime: 0, endTime: Infinity }], t, S.pw, S.ph);
            return ras.bbox ? { core: ras.core } : null;
        }
        if (o.sel && o.sel.mask && o.sel.index === sampleIndexAt(t)) return { core: o.sel.mask };
        return null;
    }
    function hexRgb(h) { var n = parseInt(h.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; }

    function drawOverlay() {
        if (!uctx) return;
        uctx.clearRect(0, 0, ui.width, ui.height);
        if (S.compare || S.state === 'rendering' || S.state === 'done') return;
        var t = S.video ? S.video.currentTime : 0;
        if (!_ovCanvas) _ovCanvas = document.createElement('canvas');
        if (_ovCanvas.width !== S.pw || _ovCanvas.height !== S.ph) { _ovCanvas.width = S.pw; _ovCanvas.height = S.ph; }
        var oc = _ovCanvas.getContext('2d');
        var id = oc.createImageData(S.pw, S.ph), d = id.data, any = false;
        var W = S.pw, H = S.ph;
        for (var oi = 0; oi < S.objects.length; oi++) {
            var o = S.objects[oi];
            var sil = silhouetteOf(o, t);
            if (!sil) continue;
            any = true;
            var rgb = hexRgb(o.color), act = oi === S.active;
            // The fill says "this goes", the outline says "exactly this": a
            // fill alone reads as a blur over the picture, an outline alone
            // disappears on busy footage.
            var fillA = act ? 110 : 80;
            var core = sil.core;
            for (var y = 0; y < H; y++) {
                var row = y * W;
                for (var x = 0; x < W; x++) {
                    var i = row + x;
                    if (!core[i]) continue;
                    // outline: a mask pixel with a non-mask 4-neighbour
                    var edge = x === 0 || y === 0 || x === W - 1 || y === H - 1 || !core[i - 1] || !core[i + 1] || !core[i - W] || !core[i + W];
                    var p = i * 4;
                    d[p] = rgb[0]; d[p + 1] = rgb[1]; d[p + 2] = rgb[2];
                    d[p + 3] = edge ? 255 : fillA;
                }
            }
        }
        if (any) {
            oc.putImageData(id, 0, 0);
            uctx.save();
            uctx.imageSmoothingEnabled = true;
            uctx.drawImage(_ovCanvas, 0, 0, ui.width, ui.height);
            uctx.restore();
        }
        // refinement points on the active object's marked frame
        var ao = activeObj();
        if (ao && ao.sel && ao.sel.points && ao.sel.points.length > 1 && ao.sel.index === sampleIndexAt(t)) {
            uctx.save();
            for (var q = 0; q < ao.sel.points.length; q++) {
                var pt = ao.sel.points[q];
                var cx = pt.x / S.pw * ui.width, cy = pt.y / S.ph * ui.height, rr = Math.max(5, ui.width / 150);
                uctx.beginPath(); uctx.arc(cx, cy, rr, 0, Math.PI * 2);
                uctx.fillStyle = pt.label ? '#34d399' : '#f87171'; uctx.fill();
                uctx.lineWidth = Math.max(1.5, rr / 3); uctx.strokeStyle = '#fff'; uctx.stroke();
            }
            uctx.restore();
        }
    }

    function drawScene() {
        if (!sctx || !S.video || S.state === 'rendering') return;
        if (!S.compare) { try { sctx.drawImage(S.video, 0, 0, scene.width, scene.height); } catch (e) { } }
        drawOverlay();
        updateTime();
    }

    function renderTracks() {
        if (!timeline) return;
        timeline.setTracks(S.objects.filter(function (o) { return o.region; }).map(function (o) {
            return { color: o.color, start: o.region.startTime || 0, end: o.region.endTime == null ? S.dur : o.region.endTime,
                lostT: o.lost ? o.lost.t : null, lostDir: o.lost ? o.lost.dir : null, active: o === activeObj() };
        }));
    }

    /* ── stage chrome: pill, busy card ───────────────────────────────────── */
    function setPill(text, kind) {
        var p = $('roPill'), s = $('roPillText');
        if (!p || !s || !text) return;
        s.textContent = text;
        p.className = 'ro-pill' + (kind && kind !== 'busy' ? ' is-' + kind : '');
        p.hidden = false;
    }
    function pillForState() {
        if (S.boxTool && (S.state === 'select' || S.state === 'ready')) { setPill(T.pill_box || '', 'add'); return; }
        if (S.state === 'select') {
            // the background is chosen but the clip is past the plan's ceiling
            if (bgOverCap() && !readyObjects().length) setPill(tr('pill_bg_cap', { cap: bgCapSec() }), 'warn');
            else setPill(T.pill_tap || '', '');
        }
        else if (S.state === 'ready') {
            if (S.mode === 'add') setPill(tr('pill_add', { name: activeObj() ? objName(activeObj()) : '' }), 'add');
            else if (S.mode === 'exclude') setPill(tr('pill_exclude', { name: activeObj() ? objName(activeObj()) : '' }), 'exclude');
            else if (bgOverCap()) setPill(tr('pill_bg_cap', { cap: bgCapSec() }), 'warn');
            else if (bgOn() && !readyObjects().length && S.bgShare != null && S.bgShare < BG_NO_PEOPLE) setPill(T.pill_bg_no_people || '', 'warn');
            else setPill(readyPill(), 'ok');
        }
    }
    /* The busy card has an OWNER. The preview runs in the reconstruction
       worker while a new tap runs in the selection worker — both may be on
       screen in turn, and the preview finishing must not wipe the card of the
       tap that replaced it. */
    function setBusy(label, frac, owner) {
        S.busy = true;
        S.busyOwner = owner || 'main';
        var b = $('roBusy');
        if (!b) return;
        b.hidden = false;
        if (label != null) $('roBusyText').textContent = label;
        var bar = $('roBusyBar'), pct = $('roBusyPct');
        if (typeof frac === 'number') {
            var f = Math.max(0, Math.min(1, frac));
            bar.style.width = Math.round(f * 100) + '%';
            pct.textContent = Math.round(f * 100) + '%';
        } else { bar.style.width = '0%'; pct.textContent = ''; }
        $('roApp').setAttribute('data-busy', '1');
    }
    function clearBusy(owner) {
        if (owner && S.busyOwner && owner !== S.busyOwner) return;
        S.busy = false;
        S.busyOwner = null;
        var b = $('roBusy'); if (b) b.hidden = true;
        var n = $('roBusyNote'); if (n) n.hidden = true;
        var c = $('roSrcCancel'); if (c) c.hidden = true;
        $('roApp').removeAttribute('data-busy');
    }
    function setWorking(on) {
        if (on) { S.working = true; }
        else { S.working = false; clearBusy(); }
    }

    function setProgress(f, label, eta) {
        var bar = $('roBar'), pct = $('roPct'), st = $('roStageLabel'), et = $('roEta');
        if (label) st.textContent = label;
        if (typeof f === 'number') {
            var p = Math.round(f * 100);
            bar.style.width = (f * 100).toFixed(1) + '%';
            pct.textContent = p + '%';
            document.title = tr('title_progress', { pct: p }, '(' + p + '%)') + ' ' + baseTitle;
            setPill(renderingPill(p), 'busy', true);
        }
        if (eta != null && isFinite(eta)) {
            et.textContent = eta > 90 ? tr('eta_min', { n: Math.ceil(eta / 60) }) : tr('eta_sec', { n: Math.max(1, Math.ceil(eta)) });
        } else if (eta === null && typeof f === 'number' && f < 0.2) et.textContent = '';
    }

    /* ── rail ────────────────────────────────────────────────────────────── */
    function setState(st) {
        S.state = st;
        var app = $('roApp');
        if (app) app.setAttribute('data-state', st);
        renderRail();
    }
    function setMode(m) {
        S.mode = m;
        var app = $('roApp'); if (app) app.setAttribute('data-mode', m);
        document.querySelectorAll('#roModeWrap [data-mode]').forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-mode') === m ? 'true' : 'false'); });
        if (S.state === 'ready') pillForState();
    }

    function renderRail() {
        var ready = readyObjects().length;
        var st = S.state;
        var s1 = $('roStep1'), s2 = $('roStep2');
        if (!s1 || !s2) return;
        var work = hasWork(), bg = bgOn();
        var s1done = ready > 0 && (st === 'ready' || st === 'rendering' || st === 'done');
        // with the background replaced, marking objects is optional, not a gate
        var s1optional = bg && !S.objects.length;
        s1.classList.toggle('is-active', !s1done && !s1optional);
        s1.classList.toggle('is-done', s1done);
        s2.classList.toggle('is-locked', !work);
        s2.classList.toggle('is-active', work);
        $('roStep1Opt').hidden = !s1optional;
        $('roStep1Sum').textContent = S.objects.length ? tr('step1_sum', { n: S.objects.length }) : '';
        $('roStep1Hint').textContent = S.objects.length ? (T.step1_hint_more || '') : (bg ? (T.step1_hint_bg || T.step1_hint || '') : (T.step1_hint || ''));
        $('roStep2T').textContent = bg ? (T.step3_title_bg || T.step2_title || '') : (T.step2_title || '');

        var list = $('roObjs');
        list.hidden = !S.objects.length;
        list.textContent = '';
        S.objects.forEach(function (o, i) {
            var li = document.createElement('li');
            li.className = 'ro-obj' + (i === S.active ? ' is-active' : '');
            li.style.setProperty('--ro-oc', o.color);
            li.tabIndex = 0;
            li.setAttribute('aria-current', i === S.active ? 'true' : 'false');
            var sw = document.createElement('span'); sw.className = 'ro-sw';
            var nm = document.createElement('span'); nm.className = 'ro-obj-n'; nm.textContent = objName(o);
            var rg = document.createElement('span'); rg.className = 'ro-obj-r';
            var ss = document.createElement('span'); ss.className = 'ro-obj-s';
            if (o.region) rg.textContent = fmtTime(o.region.startTime) + '–' + fmtTime(o.region.endTime);
            if (o.status === 'selecting' || o.status === 'tracking') ss.textContent = T.obj_tracking || '…';
            else if (o.status === 'failed') { ss.textContent = T.obj_failed || ''; ss.classList.add('warn'); }
            else if (o.lost) { ss.textContent = tr(o.lost.dir === 'back' ? 'obj_lost_before' : 'obj_lost_at', { t: fmtTime(o.lost.t) }); ss.classList.add('warn'); }
            var x = document.createElement('button');
            x.type = 'button'; x.className = 'ro-x'; x.setAttribute('aria-label', (T.obj_remove || '') + ' — ' + objName(o));
            x.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
            x.disabled = S.exporting || o.status === 'tracking' || o.status === 'selecting';
            x.addEventListener('click', function (ev) { ev.stopPropagation(); removeObject(i); });
            li.addEventListener('click', function () {
                S.active = i; renderRail(); renderTracks();
                if (o.region) { var tt = Math.max(o.region.startTime, Math.min(o.region.endTime, S.video.currentTime)); scrubTo(tt); }
                pillForState();
            });
            li.addEventListener('keydown', function (ev) {
                if (ev.key === 'Delete' || ev.key === 'Backspace') { ev.preventDefault(); removeObject(i); }
                if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); li.click(); }
            });
            li.appendChild(sw); li.appendChild(nm); li.appendChild(rg); li.appendChild(ss); li.appendChild(x);
            list.appendChild(li);
        });

        var rendering = st === 'rendering', done = st === 'done';
        $('roStep1Hint').hidden = S.objects.length > 0 && (st === 'ready' || rendering || done);
        $('roUndo').hidden = !S.undo.length || rendering || done;
        $('roUndo').disabled = !!S.working;
        $('roModeWrap').hidden = !ready || rendering || done;
        // the step says why its button waits — unless the background note in
        // step 2 already does (a clip past the plan's ceiling)
        $('roStep2Locked').hidden = work || bgOverCap();
        $('roCov').hidden = !ready || rendering || done || tightMemory();
        // the wipe is also how a chosen background is seen before it is paid for
        $('roCompare').hidden = !(ready || bg) || rendering || !!S.working;
        var boxBtn = $('roBoxTool');
        if (boxBtn) boxBtn.hidden = !(st === 'select' || st === 'ready') || !!S.working || (!S.framesReady && !S.analysisReleased);
        $('roOutRow').hidden = !work || rendering || done;
        renderOutSummary();
        renderPlanPerks();
        renderBgStep();
        var ex = $('roExport');
        ex.hidden = rendering || done;
        // After a render on a phone the frames were handed back: exporting again
        // does not need them, only a new selection does. A background past the
        // plan's duration ceiling blocks the button rather than being dropped
        // from the job behind the person's back.
        ex.disabled = !work || bgOverCap() || !!S.working || !!restoring || (!S.framesReady && !S.analysisReleased);
        $('roExportText').textContent = exportLabel();
        $('roProgress').hidden = !rendering;
        $('roDone').hidden = !done;
        var mini = document.querySelector('.ro-limit-mini');
        if (mini) mini.hidden = rendering || done;
    }

    /* ── the plan difference, said where it is felt ─────────────────────────
       Two things a plan changes here, both shown BEFORE they bind and both with
       the plan's own name: how many objects one pass removes (max_objects), and
       the export resolution (the site-wide video_max_height axis, closed by the
       shared quality gate). Nobody is sold anything where the plan is not on
       sale, and a paying visitor keeps the facts without the pitch. */
    function proLink(a, where) {
        if (!a) return;
        a.textContent = planName();
        a.href = CFG.proUrl || ('/' + (window.__LOCALE || 'en') + '/pricing');
        if (!a.__roWired) {
            a.__roWired = true;
            a.addEventListener('click', function () {
                try { window.__proSrc && window.__proSrc.set(CFG.slug + '-' + where); } catch (_) { }
                feat('ro_perk_click', { what: where });
            });
        }
    }
    var perkSeen = {};
    function perkShown(what) { if (perkSeen[what]) return; perkSeen[what] = 1; feat('ro_perk_shown', { what: what }); }
    function objectCapReached(via) {
        var n = maxObjects();
        var upsell = !planIsPro() && planSoldHere() && (CFG.proObjects | 0) > n;
        setPill(upsell ? tr('objects_cap_pill', { n: n, paid: CFG.proObjects, plan: planName() }) : tr('pill_max_objects', { n: n }), 'warn', true);
        feat('ro_object_cap', { via: via, n: n, upsell: upsell ? 1 : 0 });
        renderPlanPerks();
    }
    function renderPlanPerks() {
        var free = CFG.freeObjects | 0, pro = CFG.proObjects | 0;
        var sell = !planIsPro() && planSoldHere() && free > 0 && pro > free;
        // one line in the shared limit block, next to the resolution ceiling
        try {
            if (window.__toolLimit && window.__toolLimit.setDetail) {
                window.__toolLimit.setDetail(CFG.slug, 'objects', sell ? tr('objects_detail', { n: free, paid: pro, plan: planName() }) : '');
            }
        } catch (_) { }
        // and under the object list, the moment the ceiling is reached
        var row = $('roObjPerk');
        if (row) {
            var atCap = sell && readyObjects().length >= free && S.state !== 'rendering' && S.state !== 'done';
            row.hidden = !atCap;
            if (atCap) {
                $('roObjPerkText').textContent = tr('objects_perk', { n: free, paid: pro, plan: planName() });
                proLink($('roObjPerkCta'), 'objects');
                perkShown('objects');
            }
        }
    }

    /* Export resolution, by the SHORT side (a portrait 1080×1920 is 1080p). The
       list is the standard steps under the source plus the source itself; the
       shared gate closes what is above the plan's ceiling and says so. A source
       at or under the free ceiling has nothing to choose and shows no row. */
    var RES_STEPS = [720, 1080, 1440, 2160];
    function srcShort() { return Math.max(2, Math.min(S.vw || 0, S.vh || 0)); }
    function fillResolution() {
        var sel = $('roRes'), row = $('roResRow');
        if (!sel || !row || !S.vw) return;
        var src = srcShort();
        var steps = RES_STEPS.filter(function (v) { return v < src - 8; });
        var ceiling = (window.__qualityTier && window.__qualityTier.videoMaxHeight) ? window.__qualityTier.videoMaxHeight() : Infinity;
        row.hidden = !steps.length;
        sel.textContent = '';
        steps.forEach(function (v) {
            var o = document.createElement('option');
            o.value = String(v); o.textContent = v + 'p';
            sel.appendChild(o);
        });
        var so = document.createElement('option');
        so.value = String(src); so.setAttribute('data-quality-value', String(src)); so.setAttribute('data-ro-source', '1');
        so.textContent = src + 'p · ' + (T.res_source || '');
        sel.appendChild(so);
        // The best this visitor may have, chosen by us BEFORE the gate runs: the
        // gate's own «we lowered your setting» note is for a setting the user made.
        var best = so;
        if (!planIsPro() && isFinite(ceiling) && src > ceiling) {
            for (var i = sel.options.length - 1; i >= 0; i--) { if (+sel.options[i].value <= ceiling) { best = sel.options[i]; break; } }
        }
        sel.value = best.value;
        S.resTouched = false;
        renderOutSummary();
    }
    function outDims() {
        var sel = $('roRes');
        var src = srcShort();
        var want = sel && !$('roResRow').hidden ? (+sel.value || src) : src;
        if (window.__qualityTier && window.__qualityTier.clamp && want > (window.__qualityTier.videoMaxHeight ? window.__qualityTier.videoMaxHeight() : Infinity)) {
            want = window.__qualityTier.clamp('height', want);
        }
        if (want >= src) return { w: even(S.vw), h: even(S.vh), short: src, scaled: false };
        var k = want / src;
        return { w: even(S.vw * k), h: even(S.vh * k), short: want, scaled: true };
    }

    function renderOutSummary() {
        var el = $('roOutSum'), f = $('roFormat'), q = $('roQuality');
        if (!el || !f || !q) return;
        var fl = f.options[f.selectedIndex] ? f.options[f.selectedIndex].textContent.trim() : '';
        var ql = q.options[q.selectedIndex] ? q.options[q.selectedIndex].textContent.trim() : '';
        var rs = $('roRes'), rr = $('roResRow');
        var rl = (rs && rr && !rr.hidden && rs.options[rs.selectedIndex]) ? (rs.options[rs.selectedIndex].dataset.qtLabel || rs.options[rs.selectedIndex].textContent).split(' · ')[0].trim() : '';
        el.textContent = tr('output_sum', { format: fl, quality: ql.toLowerCase() }, fl + ', ' + ql) + (rl ? ' · ' + rl : '');
    }

    /* ── step 2 · the background ─────────────────────────────────────────── */
    var BG_IDS = ['gray', 'white', 'black', 'green', 'blur', 'custom'];
    function hexIsLight(h) {
        var n = parseInt(String(h || '#808080').slice(1), 16);
        var r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
        return (0.2126 * r + 0.7152 * g + 0.0722 * b) > 150;
    }
    function renderBgStep() {
        var sec = $('roStepBg');
        if (!sec) return;
        var on = bgOn(), st = S.state;
        var locked = st === 'rendering' || st === 'done' || st === 'opening' || st === 'empty';
        sec.classList.toggle('is-on', on);
        var sw = $('roBgSwitch');
        if (sw) { sw.checked = on; sw.disabled = locked || !window.BgReplace; }
        $('roBgBody').hidden = !on;
        $('roBgHint').textContent = on ? (T['bg_hint_' + S.bg.id] || T.bg_hint_gray || '') : (T.bg_off_hint || '');
        document.querySelectorAll('#roBgOpts [data-bg]').forEach(function (b) {
            var sel = b.getAttribute('data-bg') === S.bg.id;
            b.setAttribute('aria-checked', sel ? 'true' : 'false');
            b.tabIndex = sel ? 0 : -1;
            b.disabled = locked;
        });
        var cst = document.querySelector('#roBgOpts [data-bg="custom"]');
        if (cst) {
            // the swatch shows the colour once one was chosen, the wheel before
            var picked = S.bg.id === 'custom' || !!S.bg.customPicked;
            cst.classList.toggle('has-color', picked);
            cst.style.setProperty('--sw', S.bg.custom);
            cst.classList.toggle('is-light', picked && hexIsLight(S.bg.custom));
        }
        $('roBgBlurRow').hidden = !(on && S.bg.id === 'blur');
        var bv = $('roBgBlur'); if (bv && +bv.value !== S.bg.strength) bv.value = String(S.bg.strength);
        var bo = $('roBgBlurV'); if (bo) bo.textContent = String(S.bg.strength);

        // the plan's duration ceiling, said where it binds
        var perk = $('roBgPerk'), over = on && bgOverCap();
        if (perk) {
            perk.hidden = !over;
            var cta = $('roBgPerkCta'), trim = $('roBgTrimCta');
            cta.hidden = true; trim.hidden = true;
            if (over) {
                var b = CFG.bg || {}, free = b.freeSec | 0, paid = b.proSec | 0;
                var pro = planIsPro() || !planSoldHere();
                var dur = fmtTime(S.dur);
                var tail = readyObjects().length ? ' ' + (T.bg_cap_objs || '') : '';
                if (!pro && paid > free && S.dur <= paid + BG_CAP_SLACK) {
                    // the plan lifts exactly this ceiling
                    $('roBgPerkText').textContent = tr('bg_cap', { dur: dur, free: free, paid: paid, plan: planName() }) + tail;
                    proLink(cta, 'bg'); cta.hidden = false;
                    perkShown('bg');
                } else {
                    // longer than any plan's ceiling: the way out is a shorter clip
                    $('roBgPerkText').textContent = tr(pro ? 'bg_cap_pro' : 'bg_cap_long', { dur: dur, free: free, paid: paid, plan: planName() }) + tail;
                    trim.textContent = b.trimTitle || ''; trim.href = b.trimUrl || '#'; trim.hidden = !b.trimUrl;
                }
            }
        }
        // what the last preview found — or that the model never arrived
        var note = $('roBgNote');
        if (note) {
            var kind = !on ? '' : (S.bgErr === 'model' ? 'model'
                : ((S.bgShare != null && S.bgShare < BG_NO_PEOPLE && S.compare && S.compare.bg) ? 'nobody' : ''));
            note.hidden = !kind;
            var link = $('roBgNoteLink');
            if (kind === 'model') { $('roBgNoteText').textContent = T.bg_err_model || ''; link.hidden = true; }
            else if (kind === 'nobody') {
                $('roBgNoteText').textContent = T.bg_no_people || '';
                var vb = CFG.bg || {};
                link.textContent = vb.vbrTitle || ''; link.href = vb.vbrUrl || '#'; link.hidden = !vb.vbrUrl;
            }
        }
        // one line in the shared limit block, beside the object count
        try {
            if (window.__toolLimit && window.__toolLimit.setDetail) {
                var bb = CFG.bg || {};
                var sell = on && !planIsPro() && planSoldHere() && (bb.proSec | 0) > (bb.freeSec | 0);
                window.__toolLimit.setDetail(CFG.slug, 'bg', sell ? tr('bg_detail', { free: bb.freeSec, paid: bb.proSec, plan: planName() }) : '');
            }
        } catch (_) { }
    }

    /* Anything that moves the state between «nothing to do» and «ready»
       without an object changing: the switch, the plan snapshot arriving. */
    function refreshWorkState() {
        if (S.state === 'select' || S.state === 'ready') {
            var want = hasWork() ? 'ready' : 'select';
            if (S.state !== want) setState(want); else renderRail();
            pillForState();
        } else renderRail();
    }

    function setBg(on, why) {
        on = !!on && !!window.BgReplace;
        if (S.exporting || S.state === 'rendering' || S.state === 'done') { renderBgStep(); return; }
        if (S.bg.on === on) { renderBgStep(); return; }
        S.bg.on = on;
        var app = $('roApp');
        if (app) { app.setAttribute('data-bg', on ? '1' : '0'); if (!on) app.removeAttribute('data-bg-preview'); }
        if (!on) { S.bgShare = null; S.bgErr = null; }
        var cap = bgCapSec();
        feat('ro_bg', { on: on ? 1 : 0, bg: S.bg.id, dur: Math.round(S.dur || 0), cap: isFinite(cap) ? cap : -1,
            over: (on && S.dur > cap + BG_CAP_SLACK) ? 1 : 0, objs: readyObjects().length, why: why || 'switch', st: S.state });
        if (on) stage('background_on');
        hideCompare();
        refreshWorkState();
        if (!on) return;
        if (bgOverCap()) feat('ro_bg_capped', { dur: Math.round(S.dur), cap: bgCapSec(), via: 'switch' });
        /* On a phone whose limit is memory, the selection worker (the segmenter,
           ~1.2 GB at peak on WebKit) goes before the matting net arrives — a
           background alone does not need it, and a later tap brings it back
           (restoreAnalysis). */
        if (tightMemory() && !S.objects.length && S.framesReady) releaseForRender();
        // Show, then count: the chosen background on this very frame. While the
        // clip is still being read, loadFile shows it when the read is done.
        if (S.state === 'ready' || S.state === 'select') showCompare(S.video ? S.video.currentTime : 0, true);
    }

    function saveBgPrefs() {
        var ts = window.__toolSettings;
        if (!ts || !ts.save) return;
        try {
            ts.save(CFG.slug, 'bg_id', S.bg.id);
            ts.save(CFG.slug, 'bg_color', S.bg.custom);
            ts.save(CFG.slug, 'bg_blur', S.bg.strength);
        } catch (_) { }
    }
    function loadBgPrefs() {
        var ts = window.__toolSettings;
        if (!ts || !ts.load) return;
        try {
            var id = ts.load(CFG.slug, 'bg_id', 'gray');
            var col = String(ts.load(CFG.slug, 'bg_color', S.bg.custom) || '');
            var bl = parseInt(ts.load(CFG.slug, 'bg_blur', 5), 10);
            if (/^#[0-9a-f]{6}$/i.test(col) && col.toLowerCase() !== S.bg.custom) { S.bg.custom = col.toLowerCase(); S.bg.customPicked = true; }
            if (BG_IDS.indexOf(id) >= 0) S.bg.id = id;
            if (bl >= 1 && bl <= 10) S.bg.strength = bl;
            var ci = $('roBgColor'); if (ci) ci.value = S.bg.custom;
        } catch (_) { }
    }

    function pickBg(id, why) {
        if (BG_IDS.indexOf(id) < 0 || S.exporting) return;
        var changed = S.bg.id !== id;
        S.bg.id = id;
        saveBgPrefs();
        if (changed) feat('ro_bg_pick', { bg: id, why: why || 'click' });
        renderBgStep();
        if (!bgOn()) return;
        // a swatch change recomposes the frame already on the wipe — the net is
        // not asked again; otherwise the preview is made
        if (!recomposeBg() && (S.state === 'ready' || S.state === 'select')) showCompare(S.video ? S.video.currentTime : 0, true);
        pillForState();
    }

    function bindBg() {
        var sw = $('roBgSwitch');
        if (!sw || !window.BgReplace) {
            // no module, no step: a switch that cannot deliver is worse than none
            var sec = $('roStepBg'); if (sec) sec.hidden = true;
            return;
        }
        loadBgPrefs();
        sw.addEventListener('change', function () { setBg(sw.checked, 'switch'); });
        var group = $('roBgOpts');
        var color = $('roBgColor');
        group.addEventListener('click', function (ev) {
            var b = ev.target.closest('[data-bg]');
            if (!b || b.disabled) return;
            var id = b.getAttribute('data-bg');
            if (id === 'custom') {
                // the picker is the choice; a cancelled picker changes nothing
                try { if (color.showPicker) color.showPicker(); else color.click(); } catch (_) { try { color.click(); } catch (__) { } }
                feat('ro_bg_custom_open');
                return;
            }
            pickBg(id, 'click');
        });
        // Arrow keys move the choice, as in any radio group; the custom swatch
        // opens its picker on Enter/Space.
        group.addEventListener('keydown', function (ev) {
            var cur = ev.target.closest('[data-bg]');
            if (!cur) return;
            var ids = BG_IDS, i = ids.indexOf(cur.getAttribute('data-bg'));
            var rtl = (document.documentElement.getAttribute('dir') || '') === 'rtl';
            var step = 0;
            if (ev.key === 'ArrowRight') step = rtl ? -1 : 1;
            else if (ev.key === 'ArrowLeft') step = rtl ? 1 : -1;
            else if (ev.key === 'ArrowDown') step = 1;
            else if (ev.key === 'ArrowUp') step = -1;
            if (ev.key === 'Home' || ev.key === 'End' || step) {
                ev.preventDefault();
                var next = ev.key === 'Home' ? 0 : ev.key === 'End' ? ids.length - 1 : (i + step + ids.length) % ids.length;
                var nb = group.querySelector('[data-bg="' + ids[next] + '"]');
                pickBg(ids[next], 'key');
                if (nb) nb.focus();
            } else if ((ev.key === 'Enter' || ev.key === ' ') && cur.getAttribute('data-bg') === 'custom') {
                ev.preventDefault();
                try { if (color.showPicker) color.showPicker(); else color.click(); } catch (_) { }
            }
        });
        var onColor = function (final) {
            var v = String(color.value || '').toLowerCase();
            if (!/^#[0-9a-f]{6}$/.test(v)) return;
            S.bg.custom = v; S.bg.customPicked = true;
            if (S.bg.id !== 'custom') pickBg('custom', 'picker');
            else { if (final) saveBgPrefs(); renderBgStep(); recomposeBg(); }
        };
        color.addEventListener('input', function () { onColor(false); });
        color.addEventListener('change', function () { onColor(true); });
        var blur = $('roBgBlur');
        if (blur) {
            blur.addEventListener('input', function () {
                S.bg.strength = Math.max(1, Math.min(10, parseInt(blur.value, 10) || 5));
                var o = $('roBgBlurV'); if (o) o.textContent = String(S.bg.strength);
                recomposeBg();
            });
            blur.addEventListener('change', function () { saveBgPrefs(); feat('ro_bg_blur', { s: S.bg.strength }); });
        }
        renderBgStep();
    }

    /* ── pointer on the stage ────────────────────────────────────────────── */
    function localPoint(ev) {
        var r = ui.getBoundingClientRect();
        return { x: (ev.clientX - r.left) / r.width, y: (ev.clientY - r.top) / r.height };
    }
    /* The rectangle is a TOOL on touch screens, not a gesture.
       A finger dragging over the stage is almost always the page being
       scrolled: on phones the stage and the strip under it fill the screen, and
       both used to swallow every swipe (touch-action:none). Prod 13–15.09.2026,
       phones: 45 % of drawn rectangles came back with no silhouette (a swipe,
       not an object — 24 % on desktops), and 58 of 72 sessions that left without
       a render left scrolled no further than the stage. So on touch a tap selects
       and a swipe scrolls; the rectangle is drawn after pressing «Рамка». A mouse
       keeps both: click selects, drag draws. */
    var DRAG_PX_MOUSE = 4, DRAG_PX_TOUCH = 10, TAP_MS_TOUCH = 700;
    function setBoxTool(on, why) {
        S.boxTool = !!on;
        var app = $('roApp');
        if (app) { if (S.boxTool) app.setAttribute('data-tool', 'box'); else app.removeAttribute('data-tool'); }
        var b = $('roBoxTool');
        if (b) b.setAttribute('aria-pressed', S.boxTool ? 'true' : 'false');
        if (S.boxTool) {
            if (S.mode !== 'new') setMode('new');
            setPill(T.pill_box || '', 'add');
            feat('ro_box_tool', { on: 1, via: why || 'button' });
        } else if (why !== 'drawn') pillForState();
    }
    function bindStage() {
        ui.addEventListener('pointerdown', function (ev) {
            if (S.exporting || S.state === 'done') return;
            // Only a selection or a track in flight blocks the stage. A preview
            // or the estimate runs in the OTHER worker; the next tap simply
            // supersedes the preview (hideCompare bumps its sequence).
            if (S.working) return;
            if (!S.pw) return;
            if (ev.button != null && ev.button > 0) return;
            var touch = ev.pointerType === 'touch' || ev.pointerType === 'pen';
            var drawing = !touch || S.boxTool;
            // A finger that is about to scroll must not stop the video or grab
            // the pointer; both happen on the TAP (pointerup) instead.
            if (drawing) {
                pause();
                try { ui.setPointerCapture(ev.pointerId); } catch (_) { }
            }
            S.drag = { from: localPoint(ev), to: localPoint(ev), moved: false, touch: touch, drawing: drawing,
                cx: ev.clientX, cy: ev.clientY, t0: performance.now(), id: ev.pointerId };
        });
        ui.addEventListener('pointermove', function (ev) {
            if (!S.drag || ev.pointerId !== S.drag.id) return;
            S.drag.to = localPoint(ev);
            var dx = Math.abs(S.drag.to.x - S.drag.from.x), dy = Math.abs(S.drag.to.y - S.drag.from.y);
            var px = Math.max(Math.abs(ev.clientX - S.drag.cx), Math.abs(ev.clientY - S.drag.cy));
            if (px > (S.drag.touch ? DRAG_PX_TOUCH : DRAG_PX_MOUSE)) S.drag.moved = true;
            if (S.drag.moved && !S.drag.drawing) { S.drag.cancelled = true; return; }
            if (S.drag.moved && S.mode === 'new' && S.framesReady) {
                drawOverlay();
                uctx.save();
                var c = COLORS[S.objects.length % COLORS.length];
                uctx.strokeStyle = c; uctx.lineWidth = Math.max(2, ui.width / 400);
                uctx.setLineDash([uctx.lineWidth * 3, uctx.lineWidth * 2]);
                uctx.fillStyle = 'rgba(255,255,255,.06)';
                var x = Math.min(S.drag.from.x, S.drag.to.x) * ui.width, y = Math.min(S.drag.from.y, S.drag.to.y) * ui.height;
                uctx.fillRect(x, y, dx * ui.width, dy * ui.height);
                uctx.strokeRect(x, y, dx * ui.width, dy * ui.height);
                uctx.restore();
            }
        });
        var up = async function (ev) {
            var d = S.drag;
            if (!d || (ev && ev.pointerId !== d.id)) return;
            S.drag = null;
            // A swipe that scrolled (or wandered) is not a tap on the picture.
            if (d.cancelled) return;
            if (d.touch && !d.drawing && performance.now() - d.t0 > TAP_MS_TOUCH) return;
            if (!d.drawing) pause();
            // Also supersedes a preview still being computed: its result would
            // otherwise land on top of the selection this tap starts.
            hideCompare();
            try {
                if (d.moved && d.drawing && S.mode === 'new' && (S.framesReady || S.analysisReleased)) {
                    var bx = Math.min(d.from.x, d.to.x), by = Math.min(d.from.y, d.to.y);
                    var bw = Math.abs(d.to.x - d.from.x), bh = Math.abs(d.to.y - d.from.y);
                    if (S.boxTool) setBoxTool(false, 'drawn');
                    await boxDrawn(bx * S.pw, by * S.ph, bw * S.pw, bh * S.ph);
                } else if (S.boxTool && d.touch) {
                    // Armed and merely tapped: say again what the tool wants.
                    setPill(T.pill_box || '', 'add');
                } else {
                    if (S.boxTool) setBoxTool(false, 'tap');
                    await tapAt(Math.round(d.from.x * S.pw), Math.round(d.from.y * S.ph));
                }
            } catch (e) {
                setWorking(false);
                toast(T.err_engine || '', 'error');
                ae('tool_error', { error_type: 'ro_select', msg: String(e && e.message || e).slice(0, 160) });
                renderRail();
            }
        };
        ui.addEventListener('pointerup', up);
        ui.addEventListener('pointercancel', function (ev) {
            if (S.drag && ev && ev.pointerId !== S.drag.id) return;
            var wasDrawing = S.drag && S.drag.drawing && S.drag.moved;
            S.drag = null;
            if (wasDrawing) drawOverlay();
        });

        var boxBtn = $('roBoxTool');
        if (boxBtn) boxBtn.addEventListener('click', function () {
            if (S.exporting || S.working) return;
            hideCompare();
            setBoxTool(!S.boxTool, 'button');
        });

        // wipe knob
        var knob = $('roWipeKnob'), wiping = false;
        knob.addEventListener('pointerdown', function (ev) { wiping = true; knob.setPointerCapture(ev.pointerId); ev.stopPropagation(); ev.preventDefault(); });
        knob.addEventListener('pointermove', function (ev) {
            if (!wiping) return;
            var r = $('roBox').getBoundingClientRect();
            setWipe((ev.clientX - r.left) / r.width);
        });
        knob.addEventListener('pointerup', function () { if (wiping) { wiping = false; feat('ro_wipe_drag', { at: Math.round(S.wipe * 100) }); } });
        knob.addEventListener('keydown', function (ev) {
            if (ev.key === 'ArrowLeft' || ev.key === 'ArrowRight') { setWipe(S.wipe + (ev.key === 'ArrowLeft' ? -0.05 : 0.05)); ev.preventDefault(); }
        });
    }

    /* ── load ────────────────────────────────────────────────────────────── */
    /* Everything that belongs to the PREVIOUS video. «Другой файл» fires
       tool-uploader:file again, not :clear. */
    function resetForNewFile() {
        pause();
        estimateSeq++;
        S.compareSeq++;
        if (sessQ.probeAbort) { try { sessQ.probeAbort.abort(); } catch (_) { } }
        S.objects = []; S.active = -1; S.nextN = 1; S.undo = [];
        S.resultBlob = null; S.resultName = '';
        if (S.resultUrl) { try { URL.revokeObjectURL(S.resultUrl); } catch (_) { } S.resultUrl = null; }
        S.framesReady = false; S.pendingTap = null; S.coverage = null; S.coverageFinal = false; S.samples = [];
        dropCropVideo();
        S.restless = 0; S.plateSamples = null; S.etaFirstAt = null; S.etaFirst = null; S.t = {}; S.srcHasAudio = null; S.srcAudioCodecs = []; S.compare = null; S.working = false;
        S.thumbsDone = false; S.analysisReleased = false; S.pendingBox = null;
        // the background CHOICE outlives the file (the next clip usually wants
        // the same); what belonged to this clip's frames does not
        S.bgPrev = null; S.bgShare = null; if (S.bgErr !== 'model') S.bgErr = null;
        var app0 = $('roApp'); if (app0) app0.removeAttribute('data-bg-preview');
        ENC.probed = false; ENC.mp4 = ENC.webm = ENC.audio = ENC.audioMp4 = ENC.audioWebm = false;
        if (S.readAbort) { try { S.readAbort.abort(); } catch (e) { } S.readAbort = null; }
        if (S.session) { try { S.session.dispose(); } catch (e) { } S.session = null; }
        sessQ = { tail: Promise.resolve(), probeAbort: null };
        if (S.worker) {
            // The worker holds the segmenter (a session build and a 20 MB
            // download the SW caches). Keep it across files unless something is
            // in flight — an in-flight track cannot be cancelled any other way.
            if (S.wpending.size) {
                try { S.worker.terminate(); } catch (e) { }
                S.worker = null;
                S.seg = { state: 'idle', provider: null, promise: null, retried: false };
                S.wpending.forEach(function (p) { try { p.reject(abortedError()); } catch (_) { } });
                S.wpending.clear();
            } else {
                wcall({ type: 'dispose' }).catch(function () { });
            }
        }
        if (S.source && window.RoSource) { try { window.RoSource.release(S.source); } catch (e) { } }
        S.source = null; S.file = null; S.origFile = null; S.url = null;
        S.frameTimes = null; S.vfr = 0; S.srcFps = 0;
        var ws = $('roWorkspace'); if (ws) ws.removeAttribute('data-ro-ready');
        var rv = $('roResultVideo'); if (rv) { rv.hidden = true; try { rv.removeAttribute('src'); rv.load(); } catch (_) { } }
        $('roAfter').hidden = true; $('roWipe').hidden = true;
        S.doneCompare = false;
        if (rv) rv.classList.remove('is-wipe');
        if (timeline) { timeline.clearThumbs(); timeline.setDuration(0); }
        if (uctx) uctx.clearRect(0, 0, ui.width, ui.height);
        setMode('new');
        if (S.boxTool) setBoxTool(false, 'reset');
        clearBusy();
        document.title = baseTitle;
    }

    /* Height of whatever the site pins to the top edge right now. */
    function topInset() {
        // Bars stack: the plan banner at 0, the header right under it. Walk them
        // top-down and extend the inset while each one starts where the last
        // one ended.
        var bars = [];
        ['.pro-banner', '.header'].forEach(function (sel) {
            var el = document.querySelector(sel);
            if (!el) return;
            var cs = getComputedStyle(el);
            if ((cs.position !== 'fixed' && cs.position !== 'sticky') || cs.display === 'none' || cs.visibility === 'hidden') return;
            var r = el.getBoundingClientRect();
            if (r.height > 0 && r.bottom > 0) bars.push({ top: r.top, bottom: r.bottom });
        });
        bars.sort(function (a, b) { return a.top - b.top; });
        var h = 0;
        bars.forEach(function (b) { if (b.top <= h + 2) h = Math.max(h, b.bottom); });
        return Math.round(h);
    }
    function publishTopInset() {
        var app = $('roApp');
        if (app) app.style.setProperty('--ro-top', topInset() + 'px');
    }

    function sourceLabel(stageName) {
        if (stageName === 'probe') return T.stage_probe || '';
        if (stageName === 'engine') return T.stage_engine || '';
        if (stageName === 'remux') return T.stage_remux || '';
        if (stageName === 'convert') return T.stage_convert || '';
        return null;
    }
    function sourceErrorText(e) {
        var step = e && e.step;
        if (step === 'unreadable') return T.err_unreadable || T.err_open || '';
        if (step === 'no_video') return T.err_no_video || T.err_open || '';
        if (step === 'engine') return T.err_engine_load || T.err_open || '';
        return T.err_open || 'Could not open this video.';
    }

    async function loadFile(file) {
        resetForNewFile();
        S.origFile = file;
        if (window.__crashMarker) { try { __crashMarker.arm(CFG.slug, 'load', { mb: file.size / 1048576, video: 1 }); } catch (e) { } }
        var v = $('roVideo');
        S.video = v;
        var t0 = performance.now();
        $('roWorkspace').hidden = false;
        $('roBox').hidden = true; $('roPh').hidden = false;
        $('roTransport').hidden = true;
        setState('opening');
        setPill(T.pill_loading || '', 'busy');
        setBusy(T.stage_probe || '', null);
        $('roSrcCancel').hidden = false;
        var src;
        try {
            if (!window.RoSource) throw Object.assign(new Error('RoSource missing'), { step: 'engine' });
            src = await window.RoSource.open(file, {
                video: v,
                timeoutMs: (window.__RO_LOAD_TIMEOUT_MS | 0) || 25000,
                ffmpegUrl: CFG.ffmpegUrl, ladderUrl: CFG.ladderUrl, codecMatrixUrl: CFG.codecMatrixUrl,
                onProgress: function (stageName, frac) {
                    var label = sourceLabel(stageName);
                    setBusy(label, typeof frac === 'number' ? frac : null);
                    var n = $('roBusyNote');
                    if (stageName === 'convert') { n.textContent = T.convert_note || ''; n.hidden = false; }
                    $('roSrcCancel').hidden = false;
                }
            });
        } catch (e) {
            clearBusy();
            if (window.__crashMarker) { try { __crashMarker.disarm(CFG.slug); } catch (e2) { } }
            $('roWorkspace').hidden = true;
            setState('empty');
            if (e && e.step === 'cancel') { feat('ro_source_cancel', { ms: Math.round(performance.now() - t0) }); try { window.ToolUploader && ToolUploader.clear && ToolUploader.clear('roUpload'); } catch (e3) { } return; }
            toast(sourceErrorText(e), 'error');
            ae('tool_error', {
                error_type: 'ro_load', step: (e && e.step) || 'other', native_step: e && e.nativeStep,
                code: e && e.mediaCode, rs: e && e.rs, ns: e && e.ns, tier: e && e.tier,
                vcodec: e && e.vcodec, acodec: e && e.acodec, container: e && e.container,
                ext: String(file.name || '').split('.').pop().toLowerCase().slice(0, 8),
                mb: Math.round(file.size / 1048576), msg: String(e && e.message || e).slice(0, 160)
            });
            try { window.ToolUploader && ToolUploader.clear && ToolUploader.clear('roUpload'); } catch (e3) { }
            return;
        }
        clearBusy();
        S.source = src; S.file = src.file; S.url = src.url;
        // videoWidth is the DISPLAY width (SAR applied) and can be odd, which
        // H.264 refuses on every profile.
        S.vw = src.vw; S.vh = src.vh; S.dur = src.dur || 0;
        /* The pictures the render works on: the FILE's own frames at their own
           times (VbrFrameSource.frameTimes), not a 30-fps grid. Until 16.10.2026
           every clip was rendered at 30 a second whatever it was: a 25-fps clip got
           a repeated frame every fifth (and paid for rendering it), a 60-fps one
           lost half its frames to the grid, a phone's variable rate showed frames at
           the wrong moments (#9567 — the engine /remove-text-from-video shares).
           Above RO_MAX_FPS the render keeps the source's own frames at most that
           often (every second one of a 60-fps clip): the cost a clip had stays. */
        S.frameTimes = null; S.vfr = 0; S.srcFps = 0;
        try {
            if (window.VbrFrameSource && VbrFrameSource.frameTimes) {
                var ftr = await VbrFrameSource.frameTimes(S.file, { duration: S.dur, slug: CFG.slug });
                if (ftr) { S.frameTimes = thinFrameTimes(ftr.times, RO_MAX_FPS); S.vfr = ftr.vfr ? 1 : 0; S.srcFps = ftr.avgFps; }
            }
        } catch (_) { }
        var sc = Math.min(1, PROC_MAX / Math.max(S.vw, S.vh));
        S.pw = Math.max(16, Math.round(S.vw * sc));
        S.ph = Math.max(16, Math.round(S.vh * sc));
        sampler.width = S.pw; sampler.height = S.ph;
        fitCanvas();
        fillResolution();
        $('roPh').hidden = true; $('roBox').hidden = false;
        $('roTransport').hidden = false;
        if (timeline) { timeline.setAspect(S.vw / Math.max(1, S.vh)); timeline.setDuration(S.dur); timeline.resize(); }
        if (src.scaledTo) toast(tr('convert_downscaled', { px: src.scaledTo }), 'warning');
        // Land a third of the way in: far enough that a subject has usually
        // entered the shot, early enough to still be the part people mean.
        await seekTo(S.dur / 3);
        setState('reading');
        drawScene();
        setPill(T.pill_reading || '', 'busy');
        // The editor fits one screen by construction; bring it into view the
        // way the house allows — only if it is not already. The bars pinned to
        // the top of the window (the plan banner; on phones the header that
        // re-appears on an upward scroll) are margin, or the top of the stage
        // — where the instruction pill lives — lands underneath them.
        try {
            var ws0 = $('roWorkspace');
            ws0.style.scrollMarginTop = (topInset() + 8) + 'px';
            ws0.scrollIntoView({ block: 'nearest' });
        } catch (_) { }
        stage('file_loaded');
        feat('ro_file_loaded', {
            mb: Math.round(file.size / 1048576), dur: Math.round(S.dur), w: S.vw, h: S.vh,
            tier: src.tier, ms: src.ms, durable: src.durable, ext: String(file.name || '').split('.').pop().toLowerCase().slice(0, 8),
            vcodec: src.vcodec || '', acodec: src.acodec || '', container: src.container || '',
            extractor: src.extractor || '', scaled: src.scaledTo || 0,
            fps: Math.round((S.srcFps || 0) * 100) / 100, vfr: S.vfr || 0, nft: S.frameTimes ? S.frameTimes.length : 0
        });
        if (window.__crashMarker) { try { __crashMarker.disarm(CFG.slug); } catch (e4) { } }
        stage('file_select', { mb: Math.round(file.size / 1048576), dur: Math.round(S.dur) });
        // Does the source HAVE sound? Read it from the CONTAINER: at
        // loadedmetadata Chromium reports no decoded audio for every file.
        try {
            if (window.MediaAudioProbe) {
                var ap = await window.MediaAudioProbe.probe(S.file);
                S.srcHasAudio = !!(ap && ap.hasAudio);
                S.srcAudioCodecs = (ap && ap.codecs) || [];
                feat('ro_audio_probe', { has: S.srcHasAudio ? 1 : 0, codecs: (ap && ap.codecs || []).join(',').slice(0, 40) });
            } else S.srcHasAudio = null;
        } catch (e) { S.srcHasAudio = null; }
        await ensureWorker();
        // Model download and clip decode overlap: neither waits for the other —
        // except on a phone whose limit is memory, where the segmenter's session
        // build (1.2 GB at peak on WebKit) must not stack on top of a 4K decode.
        // There it starts once the clip is read; a tap made earlier waits for it.
        var serialSeg = tightMemory();
        if (!serialSeg) segInit();
        try {
            await readSamples();
            if (serialSeg) segInit();
        } catch (e) {
            clearBusy();
            if (isAbort(e)) return;
            toast(T.err_open || 'Could not open this video.', 'error');
            ae('tool_error', { error_type: 'ro_samples', msg: String(e && e.message || e).slice(0, 160) });
            return;
        }
        clearBusy();
        var ws = $('roWorkspace');
        if (ws) ws.setAttribute('data-ro-ready', '1');
        // The encoder pre-flight starts NOW, in the background — not on the
        // click. It used to run between «Убрать объект» and the first frame:
        // prod 09–16.09 (RU) averaged 20 s there, the longest 27 min, and ten
        // sessions never got past it (one Android Pro user: ≥2.5 min at 0 %,
        // then another file). A click now waits for at most ENC_PROBE_WAIT_MS.
        if (!S.encProbe) S.encProbe = probeEncoders().catch(function () { });
        setState(hasWork() ? 'ready' : 'select');
        drawScene();
        pillForState();
        renderRail();
        // The background was switched on for the previous clip (or while this
        // one was being read): show it on this one, the same way a first mark is.
        if (bgOn() && !S.pendingTap && !S.pendingBox) showCompare(S.video.currentTime, true);
        if (S.pendingTap) {
            var p = S.pendingTap; S.pendingTap = null;
            if (p.mode) setMode(p.mode);
            try { await tapAt(p.x, p.y); } catch (e5) { setWorking(false); renderRail(); }
        }
    }

    /* ── boot ────────────────────────────────────────────────────────────── */
    function boot() {
        scene = $('roScene'); after = $('roAfter'); ui = $('roUi');
        if (!scene || !ui || !after) return;
        sampler = document.createElement('canvas');
        sctx = scene.getContext('2d');
        actx = after.getContext('2d');
        uctx = ui.getContext('2d');
        samctx = sampler.getContext('2d', { willReadFrequently: true });
        bindStage();

        if (window.RoTimeline) {
            timeline = window.RoTimeline.create({
                el: $('roTimeline'), film: $('roFilm'), tracks: $('roTracks'), head: $('roHead'),
                onSeek: function (t, phase) {
                    if (S.exporting || S.inlineSeek) return;
                    if (phase === 'start') { pause(); hideCompare(); }
                    var v = playEl();
                    if (S.state === 'done') { try { v.currentTime = t; } catch (_) { } updateTime(); }
                    else scrubTo(t);
                    if (phase === 'end') feat('ro_timeline_seek', { st: S.state });
                },
                onStep: function (dir, big) {
                    if (S.exporting) return;
                    pause(); hideCompare();
                    var v = playEl();
                    var step = big ? 1 : 1 / Math.max(1, S.fps);
                    var t = Math.max(0, Math.min(S.dur, (v ? v.currentTime : 0) + dir * step));
                    if (S.state === 'done') { try { v.currentTime = t; } catch (_) { } updateTime(); } else scrubTo(t);
                }
            });
        }

        var slot = $('roUploadSlot');
        if (slot) {
            var onPicked = function (f) {
                if (f) loadFile(f).catch(function (e) {
                    clearBusy();
                    toast(T.err_open || 'Could not open this video. Try MP4 (H.264) or WebM.', 'error');
                    ae('tool_error', { error_type: 'ro_load', msg: (e && e.message) || String(e) });
                });
            };
            // On the ZONE, through the uploader's binder — not on the slot around
            // it. A video picked while this script was still loading reached
            // nobody: the zone redelivers to its own late listeners, and a
            // listener on an ancestor is not one of them (measured on production
            // 07.10.2026: the only tool of 312 that listened this way).
            var zone = $('roUpload');
            if (zone && typeof window.__bindUploaderFile === 'function') {
                window.__bindUploaderFile(zone, function (f, detail) { onPicked(f || (detail && detail.files && detail.files[0])); });
            } else {
                slot.addEventListener('tool-uploader:file', function (ev) {
                    onPicked(ev.detail && (ev.detail.file || (ev.detail.files && ev.detail.files[0])));
                });
            }
            slot.addEventListener('tool-uploader:clear', function () {
                resetForNewFile();
                $('roWorkspace').hidden = true;
                setState('empty');
            });
        }

        bindBg();
        $('roExport').addEventListener('click', runExport);
        $('roCancel').addEventListener('click', function () {
            // A render can run for minutes. Without this the only way out was
            // to reload the page and lose the marked objects.
            if (S.exportAbort) { try { S.exportAbort.abort(); } catch (e) { } }
        });
        $('roSrcCancel').addEventListener('click', function () {
            if (window.RoSource) { try { window.RoSource.cancel(); } catch (e) { } }
        });
        $('roCompare').addEventListener('click', function () {
            if (S.state === 'done') { setDoneCompare(!S.doneCompare); feat('ro_compare', { on: S.doneCompare ? 1 : 0, st: 'done' }); return; }
            if (S.compare) { hideCompare(); feat('ro_compare', { on: 0 }); }
            else { showCompare(S.video.currentTime, false); feat('ro_compare', { on: 1 }); }
        });
        $('roDownloadAgain').addEventListener('click', function () {
            if (window.__downloadFile && S.resultBlob) window.__downloadFile(S.resultBlob, S.resultName);
            feat('ro_download', { auto: 0 });
        });
        $('roEditMore').addEventListener('click', function () { leaveResult(); feat('ro_edit_more'); });
        $('roUndo').addEventListener('click', undo);
        ['roFormat', 'roQuality'].forEach(function (id) {
            var el = $(id);
            if (el) el.addEventListener('change', function () { renderOutSummary(); feat('ro_output', { f: ($('roFormat') || {}).value, q: ($('roQuality') || {}).value }); });
        });
        $('roPlay').addEventListener('click', function () { if (S.playing) pause(); else play(); });
        var resSel = $('roRes');
        if (resSel) resSel.addEventListener('change', function () { renderOutSummary(); if (!resSel.__qtSuppressBump) { S.resTouched = true; feat('ro_resolution', { short: +resSel.value, src: srcShort() }); } });
        // The plan snapshot lands after the page: re-decide objects and resolution.
        document.addEventListener('tier-bootstrap:ready', function () {
            if (S.vw && !S.resTouched && !S.exporting) fillResolution();
            renderPlanPerks();
            // the background's duration ceiling is the plan's: re-decide it
            if (!S.exporting) refreshWorkState();
        });
        document.querySelectorAll('#roModeWrap [data-mode]').forEach(function (b) {
            b.addEventListener('click', function () {
                var m = b.getAttribute('data-mode');
                hideCompare();
                setMode(m);
                feat('ro_chip', { m: m });
            });
        });

        var v = $('roVideo');
        if (v) {
            v.addEventListener('seeked', function () { if (!S.playing && S.state !== 'done') drawScene(); });
            v.addEventListener('ended', function () { pause(); });
        }
        var rv = $('roResultVideo');
        if (rv) {
            rv.addEventListener('timeupdate', updateTime);
            rv.addEventListener('ended', function () { S.playing = false; setPlayIcon(false); if (S.video && !S.video.paused) { try { S.video.pause(); } catch (_) { } } });
            rv.addEventListener('seeked', function () { updateTime(); if (!S.playing) syncOriginal(true); });
        }

        // Keyboard: Space plays, Escape leaves a mode or the wipe, Ctrl+Enter exports.
        document.addEventListener('keydown', function (ev) {
            var app = $('roApp');
            if (!app || $('roWorkspace').hidden) return;
            var tgt = ev.target, tag = tgt && tgt.tagName;
            var typing = tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || (tgt && tgt.isContentEditable);
            if ((ev.ctrlKey || ev.metaKey) && ev.key === 'Enter') {
                if (!$('roExport').disabled && !$('roExport').hidden) { ev.preventDefault(); runExport(); }
                return;
            }
            if (typing) return;
            var inApp = app.contains(tgt) || tgt === document.body;
            if (!inApp) return;
            var kl = window.__keyLetter ? window.__keyLetter(ev) : String(ev.key || '').toLowerCase();
            if ((ev.ctrlKey || ev.metaKey) && !ev.shiftKey && kl === 'z') {
                if (S.undo.length && (S.state === 'ready' || S.state === 'select')) { ev.preventDefault(); undo(); }
                return;
            }
            if (ev.key === ' ' && tag !== 'BUTTON' && !(tgt && tgt.getAttribute && tgt.getAttribute('role') === 'slider' && tgt.id === 'roWipeKnob')) {
                if (S.state === 'ready' || S.state === 'select' || S.state === 'done') { ev.preventDefault(); if (S.playing) pause(); else play(); }
            } else if (ev.key === 'Escape') {
                if (S.boxTool) setBoxTool(false, 'esc');
                else if (S.compare) hideCompare();
                else if (S.mode !== 'new') setMode('new');
            } else if (kl === 'b' && !ev.ctrlKey && !ev.metaKey && !ev.altKey) {
                if ((S.state === 'select' || S.state === 'ready') && !S.working) { ev.preventDefault(); hideCompare(); setBoxTool(!S.boxTool, 'key'); }
            }
        });

        window.addEventListener('pagehide', function () { pause(); });
        publishTopInset();
        window.addEventListener('resize', publishTopInset);
        renderRail();
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
})();
